// ─────────────────────────────────────────────────────────────────────────────
// src/features/mdx/components/QueryBuilder.tsx
//
// FIXES v2 :
//   ① Filtre colonnes → toujours re-fetch les membres de la dimension (API)
//      et reset du cache quand colDims change
//   ② Retrait d'un chip (×) NE vide plus la table (resultsActive reste true)
//   ③ Animation fadeSlideIn à chaque clic "Exécuter" (animKey)
//   ④ Drop zones Lignes/Colonnes toujours visibles
//   ⑤ Valeurs absolues (Math.abs) + TypeScript fixes
//
// AJOUTS v3 :
//   ⑥ Bouton collapse/expand bloc Mesures (measuresCollapsed)
//   ⑦ Bouton collapse/expand bloc Période + TopN + Solde (controlsCollapsed)
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../shared/store/hooks";
import { fetchDimensions, fetchMeasures, CUBE_NAME } from "../cubeSlice";
import {
  toggleMeasure,
  addToRows,
  removeFromRows,
  addToColumns,
  removeFromColumns,
  setDateRange,
  setTopN,
  reorderRows,
  reorderColumns,
  moveToRows,
  moveToColumns,
  resetQuery,
  toggleRowExpanded,
} from "../querySlice";
import { runQuery } from "../resultsSlice";
import { DimensionSelection, DragPayload, RowMemberInfo, measureGroupLabel } from "../types";
import Dashboard from "./Dashboard";
import ComparisonView from "./ComparisonView";
import EvolutionView from "./EvolutionView";
import MemberFilterPopover from "./MemberFilterPopover";
import SaveQueryModal from "../../saved-queries/components/SaveQueryModal";
import SavedQueriesDropdown from "../../saved-queries/components/SavedQueriesDropdown";
import { getMembers } from "../api";
import { SavedQuery } from "../../saved-queries/api";
import { getArticlesByPeriode, getSoldePeriodes } from "../../solde/api";
import THEME from "../../../shared/ui/theme";
import { applyDateMask, dayBefore, displayToIso, fixYear, isValidDate, isoToDisplay } from "../../../shared/lib/date";
import { CollapseToggleBtn } from "./CollapseToggleBtn";
import { DimensionPanel } from "./DimensionPanel";
import { buildColGroups } from "../pivotColumns";
import { colors } from "../../../shared/ui/tokens";

// ── AVDASH brand palette ──────────────────────────────────────────────────────
const NW = {
  violet: colors.brand[600],
  violetDark: colors.brand[700],
  violetMed: colors.brand[100],
  violetLight: colors.brand[100],
  violetSoft: colors.neutral[100],
  rose: colors.accent[300],
  roseDark: colors.accent[500],
  roseMed: colors.accent[100],
  roseLight: colors.accent[50],
  roseSoft: colors.neutral[100],
  white: colors.neutral[0],
  gray50: colors.neutral[50],
  gray100: colors.neutral[100],
  gray200: colors.neutral[200],
  gray300: colors.neutral[300],
  gray500: colors.neutral[500],
  gray700: colors.neutral[700],
  gray800: colors.neutral[800],
  amber: colors.warning[500],
  amberLight: colors.warning[100],
  danger: colors.danger[500],
  dangerLight: colors.danger[50],
};

type ViewMode = "table" | "dashboard" | "comparison" | "evolution";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

type FlatCell = { value: string; rowSpan: number } | null;
interface FlatRow {
  dimCells: (FlatCell | null)[];
  dataValues: any[];
  /** Membres de la ligne (drill-down), si le backend les fournit. */
  members?: RowMemberInfo[];
}

function flattenRows(
  rows: any[][],
  rowCols: number,
  keepOrigIdx: number[],
  membersOf: (row: any[]) => RowMemberInfo[] | undefined = () => undefined,
): FlatRow[] {
  if (rows.length === 0) return [];
  const pickData = (row: any[]) => keepOrigIdx.map((i) => row[rowCols + i]);
  if (rowCols === 0)
    return rows.map((row) => ({ dimCells: [], dataValues: pickData(row) }));
  if (rowCols === 1)
    return rows.map((row) => ({
      dimCells: [{ value: String(row[0] ?? ""), rowSpan: 1 }],
      dataValues: pickData(row),
      members: membersOf(row),
    }));

  const result: FlatRow[] = rows.map((row) => ({
    dimCells: Array(rowCols).fill(null) as (FlatCell | null)[],
    dataValues: pickData(row),
    members: membersOf(row),
  }));
  for (let level = 0; level < rowCols; level++) {
    let i = 0;
    while (i < rows.length) {
      const key = String(rows[i][level] ?? "");
      let j = i + 1;
      while (j < rows.length) {
        let sameParent = true;
        for (let p = 0; p < level; p++) {
          if (String(rows[j][p] ?? "") !== String(rows[i][p] ?? "")) {
            sameParent = false;
            break;
          }
        }
        if (!sameParent || String(rows[j][level] ?? "") !== key) break;
        j++;
      }
      result[i].dimCells[level] = { value: key, rowSpan: j - i };
      i = j;
    }
  }
  return result;
}

function fmtNum(val: any): string {
  if (val == null || val === "") return "";
  const n = typeof val === "number" ? val : parseFloat(val);
  if (isNaN(n)) return String(val);
  return Math.abs(n).toLocaleString("fr-FR", { maximumFractionDigits: 2 });
}

/**
 * Détecte les valeurs de dimension à exclure : vide et "All" (total, déjà dans
 * le footer / la colonne total). "Unknown"/"Inconnue" restent affichés.
 */
function isExcludedMember(val: any): boolean {
  const s = String(val ?? "").trim().toLowerCase();
  return s === "" || s === "all";
}

// ─────────────────────────────────────────────────────────────────────────────
// PivotMatrix
// ─────────────────────────────────────────────────────────────────────────────
interface PivotMatrixProps {
  dragPayload?: React.MutableRefObject<DragPayload | null>;
  onDrop?: (zone: "rows" | "columns", targetIdx: number | null) => void;
  active?: boolean;
  /** Incrémenté à chaque "Exécuter" → relance l'animation fadeSlideIn */
  animKey?: number;
}

const PivotMatrix: React.FC<PivotMatrixProps> = ({
  dragPayload,
  onDrop,
  active = true,
  animKey = 0,
}) => {
  const dispatch = useAppDispatch();
  const results = useAppSelector((s) => (s as any).results);
  const rowDims = useAppSelector((s) => s.query.rows);
  const colDims = useAppSelector((s) => s.query.columns);
  const topN = useAppSelector((s) => s.query.topN);
  const cubeDims = useAppSelector((s) => s.cube.dimensions);

  const [colOver, setColOver] = useState(false);
  const [rowOver, setRowOver] = useState(false);
  const [rowFilter, setRowFilter] = useState("");

  // ── Filtre colonnes ─────────────────────────────────────────────────────
  const [colFilterOpen, setColFilterOpen] = useState(false);
  const [colFilterQuery, setColFilterQuery] = useState("");
  const [colMembersFromApi, setColMembersFromApi] = useState<string[]>([]);
  const [colMembersSelected, setColMembersSelected] =
    useState<Set<string> | null>(null);
  const [colMembersLoading, setColMembersLoading] = useState(false);
  const colFilterRef = useRef<HTMLDivElement>(null);
  const colFilterInputRef = useRef<HTMLInputElement>(null);

  // FIX ① : reset du cache membres quand les dimensions colonnes changent
  useEffect(() => {
    setColMembersFromApi([]);
    setColMembersSelected(null);
    setColFilterQuery("");
  }, [
    JSON.stringify(
      colDims.map(
        (d) => `${d.dimensionName}|${d.hierarchyName}|${d.levelName}`,
      ),
    ),
  ]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (
        colFilterRef.current &&
        !colFilterRef.current.contains(e.target as Node)
      )
        setColFilterOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // FIX ① : toujours re-fetcher (pas seulement si cache vide)
  const handleColFilterFocus = async () => {
    setColFilterOpen(true);
    if (colDims.length === 0) return;

    // On re-fetch à chaque ouverture pour avoir des données fraîches
    setColMembersLoading(true);
    try {
      // Récupère les membres de TOUTES les dimensions colonnes (concat)
      const allMembers: string[] = [];
      for (const dim of colDims) {
        const members = await getMembers(
          CUBE_NAME,
          dim.dimensionName,
          dim.hierarchyName ?? "",
          dim.levelName ?? "",
        )
          .then(({ data }) => data ?? [])
          .catch(() => [] as string[]);
        members.forEach((m) => {
          if (!allMembers.includes(m)) allMembers.push(m);
        });
      }
      if (allMembers.length > 0) {
        setColMembersFromApi(allMembers);
        // Préserve la sélection courante si déjà filtrée
        if (colMembersSelected !== null) {
          const newSel = new Set(
            [...colMembersSelected].filter((m) => allMembers.includes(m)),
          );
          setColMembersSelected(
            newSel.size === allMembers.length ? null : newSel,
          );
        }
      }
    } catch {
      /* silencieux — on garde les données précédentes ou le fallback table */
    } finally {
      setColMembersLoading(false);
    }
  };

  const toggleColMember = (member: string) => {
    const all =
      colMembersFromApi.length > 0 ? colMembersFromApi : visibleColGroupLabels;
    const base =
      colMembersSelected === null ? new Set(all) : new Set(colMembersSelected);
    if (base.has(member)) base.delete(member);
    else base.add(member);
    setColMembersSelected(base);
  };

  const { loading, error } = results || {};
  const rawData = (results || {}).data;
  const apiColumns: string[] = rawData?.columns ?? [];
  const apiRows: any[][] = rawData?.rows ?? [];
  const hasData = apiColumns.length > 0 && apiRows.length > 0;

  const rowColCount = hasData ? Math.min(rowDims.length, apiColumns.length) : 0;
  const rowHeaders = hasData ? apiColumns.slice(0, rowColCount) : [];
  const allDataCols = hasData ? apiColumns.slice(rowColCount) : [];
  const colGroups = buildColGroups(allDataCols, rawData?.columnMembers);

  const useGroupedHeader =
    hasData &&
    (colGroups.length > 1 ||
      colGroups.some((g) => g.children.length > 1) ||
      colDims.length >= 1);

  // ── Drill-down : membres de chaque ligne (fournis par le backend) ───────
  const apiRowMembers: RowMemberInfo[][] | undefined = rawData?.rowMembers;
  const membersByRow = new Map<any[], RowMemberInfo[]>();
  if (apiRowMembers && apiRowMembers.length === apiRows.length)
    apiRows.forEach((row, i) => membersByRow.set(row, apiRowMembers[i]));
  const membersOf = (row: any[]) => membersByRow.get(row);

  // Profondeur du niveau le plus fin de chaque hiérarchie en lignes
  const maxDepthOf = (ci: number): number => {
    const sel = rowDims[ci];
    const hier = cubeDims
      .find((d) => d.name === sel?.dimensionName)
      ?.hierarchies.find((h) => h.name === sel?.hierarchyName);
    return (hier?.levels ?? []).filter((l) => l && l !== "(All)").length;
  };
  const isExpandable = (ci: number, m?: RowMemberInfo) =>
    !!m && maxDepthOf(ci) > 1 && m.levelDepth >= 1 && m.levelDepth < maxDepthOf(ci);
  const isExpanded = (ci: number, m?: RowMemberInfo) =>
    !!m && !!rowDims[ci]?.expanded?.includes(m.uniqueName);
  const hasExpansion = rowDims.some((d) => (d.expanded?.length ?? 0) > 0);

  // Profondeur de départ par dimension (pour l'indentation et les totaux)
  // Boucle simple : Math.min(...tableau) dépasse la pile d'appels au-delà
  // de quelques dizaines de milliers de lignes (« Maximum call stack size exceeded »).
  const baseDepth = Array.from({ length: rowColCount }, (_, ci) => {
    let min = Infinity;
    for (const r of apiRows) {
      const d = membersOf(r)?.[ci]?.levelDepth;
      if (typeof d === "number" && d > 0 && d < min) min = d;
    }
    return min === Infinity ? 1 : min;
  });

  const handleToggleExpand = (ci: number, m: RowMemberInfo) => {
    dispatch(toggleRowExpanded({ index: ci, uniqueName: m.uniqueName }));
    dispatch(runQuery());
  };

  // ✅ Supprimer les lignes all/vide (membre OLAP "All" = total global, déjà dans
  // le footer). Les membres "Unknown"/"Inconnue" sont affichés.
  let displayRows = apiRows.filter((row) => {
    for (let i = 0; i < rowColCount; i++) {
      if (isExcludedMember(row[i])) return false;
    }
    return true;
  });

  // Hiérarchie dépliée : pas de tri Top N (les enfants doivent rester sous leur parent)
  if (hasData && !hasExpansion && topN !== null && topN > 0 && allDataCols.length > 0) {
    displayRows = [...displayRows]
      .sort(
        (a, b) =>
          (parseFloat(b[rowColCount]) || 0) - (parseFloat(a[rowColCount]) || 0),
      )
      .slice(0, topN);
  }

  // Colonnes gardées pour chaque ligne : membres affichés + colonne « All » (total) de chaque mesure
  const keepOrigIdx = colGroups.flatMap((g) => [
    ...g.children.map((c) => c.origIdx),
    ...(g.totalIdx !== undefined ? [g.totalIdx] : []),
  ]);
  const flatRows = hasData
    ? flattenRows(displayRows, rowColCount, keepOrigIdx, membersOf)
    : [];
  const visibleColGroupLabels = colGroups.map((g) => g.label);

  // ✅ Les sous-colonnes réelles issues des résultats (DC, DG, DP, PR, SF…)
  const allChildrenLabels = colGroups.flatMap((g) =>
    g.children.map((c) => c.label),
  );
  // L'API reste appelée mais on affiche les children, pas les membres dimension
  const allAvailableMembers = (
    allChildrenLabels.length > 0 ? allChildrenLabels : visibleColGroupLabels
  ).filter((m) => !isExcludedMember(m));
  const searchedMembers = allAvailableMembers.filter(
    (m) =>
      colFilterQuery.trim() === "" ||
      m.toLowerCase().includes(colFilterQuery.trim().toLowerCase()),
  );

  // ✅ On filtre les enfants à l'intérieur de chaque groupe (Tva reste,
  //    mais on n'affiche que les children cochés : DC, DG, PR…)
  const isUnknown = (s: string) => isExcludedMember(s);

  const filteredColGroups = (
    colMembersSelected === null
      ? colGroups
      : colGroups
          .map((g) => ({
            ...g,
            children: g.children.filter((c) => colMembersSelected.has(c.label)),
          }))
          .filter((g) => g.children.length > 0)
  )
    .filter((g) => !isUnknown(g.label))
    .map((g) => ({
      ...g,
      children: g.children.filter((c) => !isUnknown(c.label)),
    }))
    .filter((g) => g.children.length > 0);

  const filteredFlatRows = !hasData
    ? []
    : rowFilter.trim() === ""
      ? flatRows
      : flatRows.filter(
          (fr) =>
            fr.dimCells.some(
              (cell) =>
                cell !== null &&
                cell.value.toLowerCase().includes(rowFilter.toLowerCase()),
            ) || fr.dimCells.every((cell) => cell === null),
        );

  const filteredKeepOrigIdx = filteredColGroups.flatMap((g) =>
    g.children.map((c) => c.origIdx),
  );

  // ── Totaux : calculés par SSAS, jamais dans le front ─────────────────────
  // • vertical   = ligne « All » (tous les membres en lignes au niveau 0)
  // • horizontal = colonne « All » de chaque mesure (ColGroup.totalIdx)
  // • croisement = ligne « All » × colonne « All »
  // Exacts même pour les ratios / moyennes. Absents → « — » (pas de recalcul).
  const grandTotalRow: any[] | undefined =
    hasData && rowColCount > 0
      ? apiRows.find((r) => {
          const ms = membersOf(r);
          return ms
            ? ms.slice(0, rowColCount).every((m) => m.levelDepth === 0)
            : r.slice(0, rowColCount).every((v) => String(v ?? "").trim().toLowerCase() === "all");
        })
      : undefined;
  /** Valeur absolue d'une cellule de l'API ; null si vide. */
  const cellValue = (v: any): number | null =>
    v == null || v === "" || isNaN(parseFloat(v)) ? null : Math.abs(parseFloat(v));
  const grandTotals = filteredKeepOrigIdx.map((origIdx) =>
    grandTotalRow ? cellValue(grandTotalRow[rowColCount + origIdx]) : null,
  );
  const showRowTotals = filteredColGroups.some((g) => g.totalIdx !== undefined);
  const groupOffsets: number[] = [];
  filteredColGroups.reduce((offset, g) => {
    groupOffsets.push(offset);
    return offset + g.children.length;
  }, 0);

  const allChildren = filteredColGroups.flatMap((g) => g.children);
  const DIM_COL_W = 140;

  const thDim: React.CSSProperties = {
    background: NW.violetDark,
    color: "#FFFFFF",
    padding: "10px 14px",
    textAlign: "left",
    fontWeight: 700,
    fontSize: 12,
    fontFamily: THEME.fonts.heading,
    border: `1px solid rgba(255,255,255,0.12)`,
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  };
  const thGroup: React.CSSProperties = {
    background: NW.rose,
    color: "#FFFFFF",
    padding: "8px 14px",
    textAlign: "center",
    fontWeight: 700,
    fontSize: 12,
    fontFamily: THEME.fonts.heading,
    border: `1px solid ${NW.roseDark}`,
    whiteSpace: "nowrap",
    letterSpacing: 0.4,
  };
  const thChild: React.CSSProperties = {
    background: NW.roseLight,
    color: NW.roseDark,
    padding: "7px 12px",
    textAlign: "center",
    fontWeight: 700,
    fontSize: 11,
    fontFamily: THEME.fonts.body,
    border: `1px solid ${NW.roseMed}`,
    whiteSpace: "nowrap",
  };

  // Colonne « Total » (total horizontal)
  const thTotal: React.CSSProperties = {
    ...thChild,
    background: NW.violetDark,
    color: "#FFFFFF",
    borderLeft: `2px solid ${NW.violet}`,
  };
  const tdTotal = (rowBg: string): React.CSSProperties => ({
    padding: "8px 14px",
    border: `1px solid ${NW.gray200}`,
    borderLeft: `2px solid ${NW.violetMed}`,
    textAlign: "right",
    fontWeight: 700,
    color: NW.violetDark,
    background: rowBg === NW.white ? NW.violetSoft : "#EEF4FB",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  });

  const renderContent = () => {
    if (loading)
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 320,
            gap: 12,
          }}
        >
          <span className="spinner" />
          <span
            style={{
              color: NW.gray500,
              fontSize: 13,
              fontFamily: THEME.fonts.body,
            }}
          >
            Chargement des données…
          </span>
        </div>
      );

    if (error)
      return (
        <div
          style={{
            padding: 32,
            textAlign: "center",
            color: NW.danger,
            background: NW.dangerLight,
            borderRadius: 8,
            fontSize: 13,
            fontFamily: THEME.fonts.body,
          }}
        >
          ⚠ {error}
        </div>
      );

    if (hasData && active)
      return (
        // FIX ③ : key=animKey force le re-mount → relance l'animation CSS
        <div
          key={animKey}
          style={{
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: 520,
            animation: "nwFadeSlideIn 0.42s cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          <table
            style={{
              borderCollapse: "collapse",
              fontSize: 12,
              fontFamily: THEME.fonts.body,
              minWidth: 480,
              tableLayout: "auto",
            }}
          >
            <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
              <tr>
                {rowHeaders.map((h, i) => (
                  <th
                    key={i}
                    rowSpan={useGroupedHeader ? 2 : 1}
                    style={{
                      ...thDim,
                      minWidth: DIM_COL_W,
                      maxWidth: 200,
                      position: "sticky",
                      left: i * DIM_COL_W,
                      zIndex: 20,
                      boxShadow: "2px 0 6px rgba(15,23,42,0.08)",
                      borderRight:
                        i === rowHeaders.length - 1
                          ? `3px solid ${NW.violet}`
                          : `1px solid rgba(255,255,255,0.15)`,
                      paddingBottom: i === 0 ? 4 : undefined,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <span>{h}</span>
                      {i === 0 && (
                        <input
                          type="text"
                          value={rowFilter}
                          onChange={(e) => setRowFilter(e.target.value)}
                          placeholder="🔍 Filtrer…"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            background: "rgba(255,255,255,0.15)",
                            border: `1px solid ${rowFilter ? NW.white : "rgba(255,255,255,0.3)"}`,
                            borderRadius: 6,
                            padding: "3px 8px",
                            fontSize: 11,
                            color: "#FFFFFF",
                            outline: "none",
                            width: "100%",
                            fontFamily: THEME.fonts.body,
                            boxSizing: "border-box",
                          }}
                        />
                      )}
                    </div>
                  </th>
                ))}
                {useGroupedHeader
                  ? filteredColGroups.map((g, gi) => (
                      <th
                        key={gi}
                        colSpan={g.children.length + (showRowTotals ? 1 : 0)}
                        style={{
                          ...thGroup,
                          borderBottom: `2px solid ${NW.roseDark}`,
                        }}
                      >
                        {g.label}
                      </th>
                    ))
                  : allChildren.map((c, ci) => (
                      <th key={ci} style={thChild}>
                        {c.label}
                      </th>
                    ))}
              </tr>
              {useGroupedHeader && (
                <tr>
                  {filteredColGroups.flatMap((g, gi) => [
                    ...g.children.map((c, ci) => (
                      <th
                        key={`${gi}-${ci}`}
                        style={{
                          ...thChild,
                          borderTop: `1px solid ${NW.roseMed}`,
                        }}
                      >
                        {c.label}
                      </th>
                    )),
                    ...(showRowTotals
                      ? [
                          <th key={`${gi}-total`} style={thTotal}>
                            ∑ Total
                          </th>,
                        ]
                      : []),
                  ])}
                </tr>
              )}
            </thead>
            <tbody>
              {filteredFlatRows.map((fr, ri) => {
                const isEven = ri % 2 === 0;
                const rowBg = isEven ? NW.white : NW.gray50;
                const dataValues = filteredKeepOrigIdx.map(
                  (origIdx) => fr.dataValues[keepOrigIdx.indexOf(origIdx)],
                );
                return (
                  <tr
                    key={ri}
                    style={{ background: rowBg }}
                    onMouseEnter={(e) =>
                      ((e.currentTarget as HTMLElement).style.background =
                        NW.violetSoft)
                    }
                    onMouseLeave={(e) =>
                      ((e.currentTarget as HTMLElement).style.background =
                        rowBg)
                    }
                  >
                    {fr.dimCells.map((cell, ci) =>
                      cell === null ? null : (
                        <td
                          key={ci}
                          rowSpan={cell.rowSpan}
                          style={{
                            padding: "8px 14px",
                            border: `1px solid ${NW.violetMed}`,
                            borderRight:
                              ci === rowColCount - 1
                                ? `3px solid ${NW.violet}`
                                : `1px solid ${NW.violetMed}`,
                            fontWeight: ci === 0 ? 700 : 500,
                            color: NW.violetDark,
                            background: isEven ? NW.violetSoft : "#EEF4FB",
                            verticalAlign: "middle",
                            position: "sticky",
                            left: ci * DIM_COL_W,
                            zIndex: 2,
                            whiteSpace: "nowrap",
                            minWidth: DIM_COL_W,
                            // retrait selon la profondeur (membres dépliés)
                            paddingLeft:
                              (ci > 0 ? 22 : 14) +
                              Math.max(0, (fr.members?.[ci]?.levelDepth ?? baseDepth[ci]) - baseDepth[ci]) * 18,
                            boxShadow:
                              ci === rowColCount - 1
                                ? "2px 0 6px rgba(24,75,140,0.07)"
                                : undefined,
                          }}
                        >
                          {(() => {
                            const m = fr.members?.[ci];
                            if (!isExpandable(ci, m)) {
                              return cell.value || (ci > 0 ? "—" : "");
                            }
                            const open = isExpanded(ci, m);
                            return (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                <button
                                  onClick={() => handleToggleExpand(ci, m!)}
                                  aria-expanded={open}
                                  title={open ? "Replier" : "Déplier"}
                                  style={{
                                    width: 18,
                                    height: 18,
                                    padding: 0,
                                    border: `1px solid ${NW.violetMed}`,
                                    borderRadius: 4,
                                    background: NW.white,
                                    color: NW.violet,
                                    fontSize: 9,
                                    lineHeight: 1,
                                    cursor: "pointer",
                                    flexShrink: 0,
                                  }}
                                >
                                  {open ? "▼" : "▶"}
                                </button>
                                {cell.value}
                              </span>
                            );
                          })()}
                        </td>
                      ),
                    )}
                    {filteredColGroups.flatMap((g, gi) => {
                      const cells = g.children.map((_, k) => {
                        const di = groupOffsets[gi] + k;
                        const val = dataValues[di];
                        const raw =
                          val != null && val !== "" ? parseFloat(val) : null;
                        const empty = raw === null || isNaN(raw as number);
                        const num = empty ? null : Math.abs(raw as number);
                        return (
                          <td
                            key={di}
                            style={{
                              padding: "8px 14px",
                              border: `1px solid ${NW.gray200}`,
                              textAlign: "right",
                              color: empty ? NW.gray500 : NW.gray800,
                              background: rowBg,
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {num !== null ? (
                              num.toLocaleString("fr-FR", {
                                maximumFractionDigits: 2,
                              })
                            ) : (
                              <span style={{ color: NW.gray300 }}>—</span>
                            )}
                          </td>
                        );
                      });
                      if (!showRowTotals) return cells;
                      // Colonne « All » renvoyée par SSAS pour cette ligne
                      const rowTotal =
                        g.totalIdx !== undefined
                          ? cellValue(fr.dataValues[keepOrigIdx.indexOf(g.totalIdx)])
                          : null;
                      return [
                        ...cells,
                        <td key={`${gi}-total`} style={tdTotal(rowBg)}>
                          {rowTotal !== null ? (
                            fmtNum(rowTotal)
                          ) : (
                            <span style={{ color: NW.gray300 }}>—</span>
                          )}
                        </td>,
                      ];
                    })}
                  </tr>
                );
              })}
            </tbody>
            {grandTotalRow && displayRows.length > 1 && (
              <tfoot style={{ position: "sticky", bottom: 0, zIndex: 5 }}>
                <tr>
                  {rowHeaders.map((_, i) => (
                    <td
                      key={i}
                      style={{
                        padding: "9px 14px",
                        background: NW.violetDark,
                        color: "#FFFFFF",
                        fontWeight: 800,
                        fontSize: 12,
                        fontFamily: THEME.fonts.heading,
                        border: `1px solid ${NW.violet}`,
                        borderRight:
                          i === rowColCount - 1
                            ? `3px solid ${NW.rose}`
                            : undefined,
                        textAlign: i === 0 ? "left" : "center",
                        position: "sticky",
                        left: i * DIM_COL_W,
                        zIndex: 10,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {i === 0 ? "∑ TOTAL GÉNÉRAL" : ""}
                    </td>
                  ))}
                  {filteredColGroups.flatMap((g, gi) => {
                    const footCell = (key: string, val: number | null, isTotal = false) => (
                      <td
                        key={key}
                        style={{
                          padding: "9px 14px",
                          textAlign: "right",
                          background: NW.violetDark,
                          color: isTotal ? "#FFFFFF" : NW.roseMed,
                          fontWeight: isTotal ? 800 : 700,
                          fontSize: 12,
                          border: `1px solid ${NW.violet}`,
                          borderLeft: isTotal ? `2px solid ${NW.violet}` : undefined,
                          fontVariantNumeric: "tabular-nums",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {val !== null ? fmtNum(val) : "—"}
                      </td>
                    );
                    const cells = g.children.map((_, k) =>
                      footCell(String(groupOffsets[gi] + k), grandTotals[groupOffsets[gi] + k]),
                    );
                    if (!showRowTotals) return cells;
                    // Croisement : ligne « All » × colonne « All » (total général SSAS de la mesure)
                    const corner =
                      g.totalIdx !== undefined && grandTotalRow
                        ? cellValue(grandTotalRow[rowColCount + g.totalIdx])
                        : null;
                    return [...cells, footCell(`${gi}-total`, corner, true)];
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      );

    // Grille vide décorative
    return (
      <div
        style={{
          position: "relative",
          minHeight: 360,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: NW.white,
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: [
              `repeating-linear-gradient(0deg, ${NW.gray200} 0px, ${NW.gray200} 1px, transparent 1px, transparent 26px)`,
              `repeating-linear-gradient(90deg, ${NW.gray200} 0px, ${NW.gray200} 1px, transparent 1px, transparent 64px)`,
            ].join(", "),
            opacity: 0.35,
          }}
        />
        <div
          style={{
            position: "relative",
            textAlign: "center",
            color: NW.gray500,
            fontSize: 12,
            lineHeight: 1.8,
            padding: "0 28px",
          }}
        >
          Glissez des dimensions dans{" "}
          <strong style={{ color: NW.violet }}>↕ Lignes</strong> et{" "}
          <strong style={{ color: NW.rose }}>↔ Colonnes</strong>, sélectionnez
          une <strong style={{ color: NW.amber }}>Mesure</strong> puis cliquez
          sur <strong>Exécuter</strong>.
        </div>
      </div>
    );
  };

  return (
    <>
      {/* FIX ③ : keyframes animation table */}
      <style>{`
        @keyframes nwFadeSlideIn {
          from { opacity: 0; transform: translateY(12px) scale(0.99); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
        @keyframes nwRunPulse {
          0%   { box-shadow: 0 4px 14px rgba(24,75,140,0.25); }
          40%  { box-shadow: 0 6px 28px rgba(24,75,140,0.55), 0 0 0 3px rgba(125,168,217,0.2); }
          100% { box-shadow: 0 4px 14px rgba(24,75,140,0.25); }
        }
        .nw-run-btn-active {
          animation: nwRunPulse 0.7s ease-out;
        }
        @keyframes nwDateShake {
          0%   { transform: translateX(0); }
          15%  { transform: translateX(-5px); }
          30%  { transform: translateX(5px); }
          45%  { transform: translateX(-4px); }
          60%  { transform: translateX(4px); }
          75%  { transform: translateX(-2px); }
          90%  { transform: translateX(2px); }
          100% { transform: translateX(0); }
        }
        .nw-date-shake {
          animation: nwDateShake 0.35s ease;
        }
      `}</style>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          fontFamily: THEME.fonts.body,
        }}
      >
        {/* Barre info (topN + filtre colonnes + compteur) */}
        {hasData && active && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 12,
              color: NW.gray500,
              flexWrap: "wrap",
            }}
          >
            {topN !== null && (
              <span style={{ color: NW.violet, fontWeight: 700 }}>
                🏆 Top {topN} / {apiRows.length} lignes
              </span>
            )}

            {/* Input recherche colonnes — FIX ① */}
            {colDims.length > 0 && (
              <div
                ref={colFilterRef}
                style={{ position: "relative", display: "inline-block" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "3px 10px",
                    border: `1.5px solid ${colFilterOpen ? NW.rose : NW.gray200}`,
                    borderRadius: 8,
                    background: colFilterOpen ? NW.roseSoft : NW.white,
                    transition: "border-color 0.15s",
                    boxShadow: colFilterOpen
                      ? `0 0 0 2px ${NW.roseMed}`
                      : undefined,
                  }}
                >
                  <span style={{ color: NW.rose, fontSize: 13 }}>🔍</span>
                  <input
                    ref={colFilterInputRef}
                    type="text"
                    value={colFilterQuery}
                    onFocus={handleColFilterFocus}
                    onChange={(e) => {
                      setColFilterQuery(e.target.value);
                      setColFilterOpen(true);
                    }}
                    placeholder="Filtrer colonnes…"
                    style={{
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      fontSize: 12,
                      fontFamily: THEME.fonts.body,
                      color: NW.gray800,
                      width: 160,
                    }}
                  />
                  {colMembersSelected !== null && (
                    <span
                      style={{
                        background: NW.roseMed,
                        color: NW.roseDark,
                        borderRadius: 99,
                        fontSize: 11,
                        padding: "1px 8px",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {colMembersSelected.size}✓
                    </span>
                  )}
                  {(colFilterQuery || colMembersSelected !== null) && (
                    <button
                      onClick={() => {
                        // FIX ② : effacer le filtre ne vide PAS la table
                        setColFilterQuery("");
                        setColMembersSelected(null);
                        colFilterInputRef.current?.focus();
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: NW.gray500,
                        fontSize: 14,
                        lineHeight: 1,
                        padding: 0,
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
                {colFilterOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      left: 0,
                      zIndex: 9999,
                      background: NW.white,
                      border: `1.5px solid ${NW.roseMed}`,
                      borderRadius: 10,
                      boxShadow: "0 8px 30px rgba(125,168,217,0.15)",
                      minWidth: 260,
                      maxHeight: 360,
                      overflowY: "auto",
                      padding: "8px 0",
                    }}
                  >
                    <div
                      style={{
                        padding: "6px 14px 8px",
                        borderBottom: `1px solid ${NW.gray100}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: 12,
                          color: NW.roseDark,
                          flex: 1,
                        }}
                      >
                        ↔ Membres colonnes
                        {colDims.length > 0 && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 400,
                              color: NW.gray500,
                              marginLeft: 6,
                            }}
                          >
                            (
                            {colDims
                              .map((d) => d.hierarchyName ?? d.dimensionName)
                              .join(", ")}
                            )
                          </span>
                        )}
                      </span>
                      <button
                        onClick={() => {
                          setColMembersSelected(null);
                          setColFilterQuery("");
                        }}
                        style={{
                          fontSize: 10,
                          padding: "1px 7px",
                          border: `1px solid ${NW.gray200}`,
                          borderRadius: 4,
                          cursor: "pointer",
                          background: NW.white,
                          color: NW.gray700,
                        }}
                      >
                        Tout
                      </button>
                      <button
                        onClick={() => setColMembersSelected(new Set())}
                        style={{
                          fontSize: 10,
                          padding: "1px 7px",
                          border: `1px solid ${NW.gray200}`,
                          borderRadius: 4,
                          cursor: "pointer",
                          background: NW.white,
                          color: NW.gray700,
                        }}
                      >
                        Aucun
                      </button>
                    </div>
                    {colMembersLoading && (
                      <div
                        style={{
                          padding: "10px 14px",
                          color: NW.gray500,
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span
                          className="spinner"
                          style={{ width: 14, height: 14 }}
                        />
                        Chargement des membres…
                      </div>
                    )}
                    {!colMembersLoading && searchedMembers.length === 0 && (
                      <div
                        style={{
                          padding: "10px 14px",
                          color: NW.gray500,
                          fontSize: 12,
                          fontStyle: "italic",
                        }}
                      >
                        Aucun résultat pour « {colFilterQuery} »
                      </div>
                    )}
                    {!colMembersLoading &&
                      searchedMembers.map((member) => {
                        const checked =
                          colMembersSelected === null ||
                          colMembersSelected.has(member);
                        return (
                          <label
                            key={member}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 9,
                              padding: "7px 14px",
                              cursor: "pointer",
                              fontSize: 12,
                              color: checked ? NW.roseDark : NW.gray500,
                              fontWeight: checked ? 600 : 400,
                              background: checked ? NW.roseSoft : "transparent",
                              transition: "background 0.1s",
                              borderBottom: `1px solid ${NW.gray100}`,
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = NW.roseLight)
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = checked
                                ? NW.roseSoft
                                : "transparent")
                            }
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleColMember(member)}
                              style={{
                                accentColor: NW.rose,
                                width: 14,
                                height: 14,
                                flexShrink: 0,
                              }}
                            />
                            <span
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {member}
                            </span>
                          </label>
                        );
                      })}
                  </div>
                )}
              </div>
            )}

            <span style={{ marginLeft: "auto" }}>
              {filteredFlatRows.length} lignes · {allChildren.length} mesures
            </span>
          </div>
        )}

        {/* Conteneur principal avec drop zones toujours visibles */}
        <div
          style={{
            border: `1.5px solid ${NW.violetMed}`,
            borderRadius: 12,
            overflow: "hidden",
            boxShadow: `0 4px 20px rgba(24,75,140,0.07)`,
          }}
        >
          {/* ↔ COLONNES */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setColOver(true);
            }}
            onDragLeave={() => setColOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setColOver(false);
              if (onDrop) onDrop("columns", null);
            }}
            style={{
              minHeight: 50,
              borderBottom: `2px dashed ${colOver ? NW.rose : NW.roseMed}`,
              background: colOver
                ? `${NW.rose}10`
                : `linear-gradient(90deg, ${NW.roseSoft} 0%, #FFFFFF 80%)`,
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 18px",
              flexWrap: "wrap",
              transition: "border-color 0.15s, background 0.15s",
              cursor: "default",
            }}
          >
            <span
              style={{
                color: NW.rose,
                fontWeight: 700,
                fontSize: 12,
                whiteSpace: "nowrap",
                letterSpacing: 0.3,
              }}
            >
              ↔ Colonnes
            </span>
            {colDims.length === 0 ? (
              <span
                style={{
                  color: colOver ? NW.rose : NW.gray500,
                  fontSize: 11,
                  fontStyle: "italic",
                  transition: "color 0.15s",
                }}
              >
                ↑ Glissez une dimension ici
              </span>
            ) : (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {colDims.map((sel, i) => (
                  <span
                    key={i}
                    style={{
                      background: NW.rose,
                      color: "#FFFFFF",
                      borderRadius: 7,
                      padding: "4px 12px",
                      fontSize: 11,
                      fontWeight: 700,
                      boxShadow: "0 2px 6px rgba(125,168,217,0.25)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    {sel.hierarchyName ?? sel.dimensionName}
                    {sel.levelName && sel.levelName !== sel.hierarchyName && (
                      <span style={{ fontSize: 10, opacity: 0.72 }}>
                        · {sel.levelName}
                      </span>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* LIGNE : ↕ LIGNES (gauche) | Contenu (droite) */}
          <div style={{ display: "flex", minHeight: 380 }}>
            {/* ↕ LIGNES */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setRowOver(true);
              }}
              onDragLeave={() => setRowOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setRowOver(false);
                if (onDrop) onDrop("rows", null);
              }}
              style={{
                width: 84,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                paddingTop: 36,
                gap: 8,
                borderRight: `2px dashed ${rowOver ? NW.violet : NW.violetMed}`,
                background: rowOver
                  ? `${NW.violet}10`
                  : `linear-gradient(180deg, ${NW.violetSoft} 0%, #FFFFFF 80%)`,
                transition: "border-color 0.15s, background 0.15s",
                cursor: "default",
              }}
            >
              <span
                style={{
                  writingMode: "vertical-rl",
                  textOrientation: "mixed",
                  transform: "rotate(180deg)",
                  color: NW.violet,
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: 0.3,
                  whiteSpace: "nowrap",
                }}
              >
                ↕ Lignes
              </span>
              {rowDims.length === 0 ? (
                <span
                  style={{
                    writingMode: "vertical-rl",
                    textOrientation: "mixed",
                    transform: "rotate(180deg)",
                    color: rowOver ? NW.violet : NW.gray500,
                    fontSize: 10,
                    fontStyle: "italic",
                    whiteSpace: "nowrap",
                    marginTop: 6,
                    transition: "color 0.15s",
                  }}
                >
                  ← Glissez une dimension ici
                </span>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 5,
                    alignItems: "center",
                    marginTop: 8,
                  }}
                >
                  {rowDims.map((sel, i) => (
                    <span
                      key={i}
                      style={{
                        background: NW.violet,
                        color: "#FFFFFF",
                        borderRadius: 7,
                        padding: "5px 9px",
                        fontSize: 10,
                        fontWeight: 700,
                        writingMode: "vertical-rl",
                        textOrientation: "mixed",
                        transform: "rotate(180deg)",
                        whiteSpace: "nowrap",
                        maxHeight: 130,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        boxShadow: "0 2px 6px rgba(24,75,140,0.25)",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 3,
                      }}
                    >
                      {sel.hierarchyName ?? sel.dimensionName}
                      {sel.levelName && sel.levelName !== sel.hierarchyName && (
                        <span style={{ fontSize: 9, opacity: 0.7 }}>
                          {" "}
                          · {sel.levelName}
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* CONTENU */}
            <div style={{ flex: 1, minWidth: 0, background: NW.white }}>
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Quick period helper
// ─────────────────────────────────────────────────────────────────────────────
function getQuickPeriod(t: "week" | "month" | "year"): {
  dateFrom: string;
  dateTo: string;
} {
  const d = new Date();
  const from = new Date(d);
  if (t === "week") from.setDate(d.getDate() - 7);
  else if (t === "month") from.setMonth(d.getMonth() - 1);
  else from.setFullYear(d.getFullYear() - 1);
  return {
    dateFrom: from.toISOString().slice(0, 10),
    dateTo: d.toISOString().slice(0, 10),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Démarque helper — construit les phases de démarque d'une période de solde
// ─────────────────────────────────────────────────────────────────────────────
interface DemarquePhase {
  label: string;
  icon: string;
  dateFrom: string;
  dateTo: string;
}

function buildDemarquePhases(p: {
  date_deb_priv: string | null;
  date_debut: string | null;
  date_debut2: string | null;
  date_debut3: string | null;
  date_debut4: string | null;
  date_fin: string | null;
}): DemarquePhase[] {
  const fin = p.date_fin ? p.date_fin.slice(0, 10) : "";
  const phases: DemarquePhase[] = [];
  if (p.date_debut)
    phases.push({
      label: "Démarque 1",
      icon: "🏷️",
      dateFrom: p.date_debut.slice(0, 10),
      dateTo: p.date_debut2 ? dayBefore(p.date_debut2) : fin,
    });
  if (p.date_debut2)
    phases.push({
      label: "Démarque 2",
      icon: "🏷️",
      dateFrom: p.date_debut2.slice(0, 10),
      dateTo: p.date_debut3 ? dayBefore(p.date_debut3) : fin,
    });
  if (p.date_debut3)
    phases.push({
      label: "Démarque 3",
      icon: "🏷️",
      dateFrom: p.date_debut3.slice(0, 10),
      dateTo: p.date_debut4 ? dayBefore(p.date_debut4) : fin,
    });
  if (p.date_debut4)
    phases.push({
      label: "Démarque 4",
      icon: "🏷️",
      dateFrom: p.date_debut4.slice(0, 10),
      dateTo: fin,
    });
  return phases;
}

// ─────────────────────────────────────────────────────────────────────────────
// QueryBuilder — composant principal
// ─────────────────────────────────────────────────────────────────────────────
const QueryBuilder: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    dimensions,
    measures,
    loading: cubeLoading,
  } = useAppSelector((s) => s.cube);
  const {
    measures: selMeasures,
    rows,
    columns,
    dateFrom,
    dateTo,
    topN,
  } = useAppSelector((s) => s.query);

  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const dragPayload = useRef<DragPayload | null>(null);
  const [measureQuery, setMeasureQuery] = useState("");
  const [measureDropOpen, setMeasureDropOpen] = useState(false);
  const [dimPanelCollapsed, setDimPanelCollapsed] = useState(false);
  const measureInputRef = useRef<HTMLInputElement>(null);
  const measureDropRef = useRef<HTMLDivElement>(null);
  const [resultsActive, setResultsActive] = useState(false);

  // FIX ③ : clé d'animation, incrémentée à chaque "Exécuter"
  const [animKey, setAnimKey] = useState(0);
  // Pour l'animation du bouton Exécuter
  const [runBtnAnim, setRunBtnAnim] = useState(false);
  const [measurePopupGroup, setMeasurePopupGroup] = useState<string | null>(
    null,
  );
  // Snapshot des mesures sélectionnées à l'ouverture du popup (pour "Annuler")
  const [popupInitialMeasures, setPopupInitialMeasures] = useState<string[]>(
    [],
  );
  const [hoveredPlusGroup, setHoveredPlusGroup] = useState<string | null>(null);

  // ── AJOUT ⑥ : collapse du bloc Mesures ───────────────────────────────────
  const [measuresCollapsed, setMeasuresCollapsed] = useState(false);

  // ── AJOUT ⑦ : collapse du bloc Période / TopN / Solde ────────────────────
  const [controlsCollapsed, setControlsCollapsed] = useState(false);

  const measurePopupRef = useRef<HTMLDivElement>(null);
  // ── Enregistrer / charger requêtes sauvegardées ───────────────────────────
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveRefreshToken, setSaveRefreshToken] = useState(0);
  // ── Périodes de comparaison sauvegardées ─────────────────────────────────
  // Alimenté lors du chargement d'une requête sauvegardée, passé à ComparisonView
  const [savedComparisonPeriods, setSavedComparisonPeriods] = useState<Array<{
    dateFrom: string;
    dateTo: string;
  }> | null>(null);
  // Token incrémenté pour déclencher l'auto-run dans ComparisonView
  const [comparisonAutoRunToken, setComparisonAutoRunToken] = useState(0);

  // ── Solde / Périodes ─────────────────────────────────────────────────────
  interface SoldePeriode {
    code_periode: string;
    date_debut: string | null;
    date_fin: string | null;
    date_deb_priv: string | null;
    date_debut2: string | null;
    date_debut3: string | null;
    date_debut4: string | null;
    cod_dep: string | null;
  }
  const [soldeOpen, setSoldeOpen] = useState(false);
  const [soldePeriodes, setSoldePeriodes] = useState<SoldePeriode[]>([]);
  const [soldeLoading, setSoldeLoading] = useState(false);
  const [soldeError, setSoldeError] = useState<string | null>(null);
  const [soldeSelected, setSoldeSelected] = useState<SoldePeriode | null>(null);
  const [soldeArticleCodes, setSoldeArticleCodes] = useState<Set<string>>(
    new Set(),
  );
  const soldeDropRef = useRef<HTMLDivElement>(null);

  // ── Démarque dropdown ─────────────────────────────────────────────────────
  const [demarqueOpen, setDemarqueOpen] = useState(false);
  const demarqueDropRef = useRef<HTMLDivElement>(null);
  const [selectedDemarquePhase, setSelectedDemarquePhase] = useState<
    string | null
  >(null);

  // Fermer le dropdown solde si clic dehors
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (
        soldeDropRef.current &&
        !soldeDropRef.current.contains(e.target as Node)
      )
        setSoldeOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Fermer le dropdown démarque si clic dehors
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (
        demarqueDropRef.current &&
        !demarqueDropRef.current.contains(e.target as Node)
      )
        setDemarqueOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleSoldeOpen = async () => {
    setSoldeOpen((v) => !v);
    if (soldePeriodes.length > 0) return; // déjà chargé
    setSoldeLoading(true);
    setSoldeError(null);
    try {
      const { data } = await getSoldePeriodes();
      // Filtrer les périodes non vides (avec date_debut et date_fin)
      const nonVides = data.filter((p) => p.date_debut && p.date_fin);
      setSoldePeriodes(nonVides);
    } catch (err: any) {
      setSoldeError(err.message ?? "Erreur chargement périodes");
    } finally {
      setSoldeLoading(false);
    }
  };

  const handleSoldePeriodeSelect = async (p: SoldePeriode) => {
    setSoldeSelected(p);
    setSoldeOpen(false);
    setSelectedDemarquePhase(null);
    setDemarqueOpen(false);

    // Remplir automatiquement les champs dates
    const from = p.date_debut ? p.date_debut.slice(0, 10) : "";
    const to = p.date_fin ? p.date_fin.slice(0, 10) : "";
    dispatch(setDateRange({ dateFrom: from, dateTo: to }));

    // Charger les articles de la période pour filtrer artparent2
    try {
      const { data: articles } = await getArticlesByPeriode(p.code_periode);
      const codes = new Set<string>(
        articles.map((a: any) => (a.cod_art ?? "").toString()).filter(Boolean),
      );
      setSoldeArticleCodes(codes);
    } catch {
      setSoldeArticleCodes(new Set());
    }

    setResultsActive(false);
  };

  // Effacer la sélection solde
  const handleSoldeClear = () => {
    setSoldeSelected(null);
    setSelectedDemarquePhase(null);
    setSoldeArticleCodes(new Set());
    dispatch(setDateRange({ dateFrom: "", dateTo: "" }));
    setResultsActive(false);
  };

  useEffect(() => {
    dispatch(fetchDimensions());
    dispatch(fetchMeasures());
  }, [dispatch]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        !measureInputRef.current?.contains(t) &&
        !measureDropRef.current?.contains(t)
      )
        setMeasureDropOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (
        measurePopupRef.current &&
        !measurePopupRef.current.contains(e.target as Node)
      )
        setMeasurePopupGroup(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const measureSuggestions = useMemo(() => {
    const q = measureQuery.trim().toLowerCase();
    if (!q) return measures.slice(0, 15);
    return measures.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        (m.caption ?? "").toLowerCase().includes(q) ||
        (m.measureGroup ?? "").toLowerCase().includes(q),
    );
  }, [measureQuery, measures]);

  const handleMeasureSelect = (name: string) => {
    dispatch(toggleMeasure(name));
    setResultsActive(false);
    setMeasureQuery("");
    setMeasureDropOpen(false);
    measureInputRef.current?.focus();
  };

  // Ajout depuis le panneau des dimensions (niveau choisi inclus)
  const handleAddSelection = (sel: DimensionSelection, target: "rows" | "columns") => {
    if (target === "rows") dispatch(addToRows(sel));
    else dispatch(addToColumns(sel));
    setResultsActive(false);
  };

  // FIX ② : ne plus appeler setResultsActive(false) → la table reste visible
  const handleRemoveFromRows = (i: number) => {
    dispatch(removeFromRows(i));
    // NE PAS vider la table : setResultsActive(false) retiré
  };

  const handleRemoveFromColumns = (i: number) => {
    dispatch(removeFromColumns(i));
    // NE PAS vider la table : setResultsActive(false) retiré
  };

  // FIX ③ : incrémenter animKey à chaque exécution
  const handleRunQuery = () => {
    dispatch(runQuery());
    setResultsActive(true);
    setAnimKey((k) => k + 1);
    // Animation bouton
    setRunBtnAnim(true);
    setTimeout(() => setRunBtnAnim(false), 700);
  };

  const handleLoadSavedQuery = (q: SavedQuery) => {
    dispatch(resetQuery());
    // Mesures
    q.measures.forEach((m: string) => dispatch(toggleMeasure(m)));
    // Dimensions lignes
    (q.rowsDimensions ?? []).forEach((d: any) => dispatch(addToRows(d)));
    // Dimensions colonnes
    (q.columnsDimensions ?? []).forEach((d: any) => dispatch(addToColumns(d)));
    // Période
    if (q.dateFrom || q.dateTo) {
      dispatch(
        setDateRange({ dateFrom: q.dateFrom ?? "", dateTo: q.dateTo ?? "" }),
      );
    }
    // Top N
    if (q.topN !== null && q.topN !== undefined) {
      dispatch(setTopN(q.topN));
    }

    // ── Solde : restaurer la période sélectionnée ─────────────────────────
    const savedSoldeCode = (q as any).soldeCodePeriode as
      | string
      | null
      | undefined;
    if (savedSoldeCode) {
      const found = soldePeriodes.find(
        (p) => p.code_periode === savedSoldeCode,
      );
      if (found) {
        setSoldeSelected(found);
      } else {
        setSoldeSelected({
          code_periode: savedSoldeCode,
          date_debut: q.dateFrom ?? null,
          date_fin: q.dateTo ?? null,
          date_deb_priv: null,
          date_debut2: null,
          date_debut3: null,
          date_debut4: null,
          cod_dep: null,
        });
      }
    } else {
      setSoldeSelected(null);
      setSoldeArticleCodes(new Set());
    }

    // ── Périodes de comparaison : parser le JSON et injecter dans ComparisonView
    const rawPeriods = (q as any).comparisonPeriods;
    if (rawPeriods) {
      try {
        const parsed: Array<{ dateFrom: string; dateTo: string }> =
          typeof rawPeriods === "string" ? JSON.parse(rawPeriods) : rawPeriods;
        setSavedComparisonPeriods(Array.isArray(parsed) ? parsed : null);
      } catch {
        setSavedComparisonPeriods(null);
      }
    } else {
      setSavedComparisonPeriods(null);
    }

    // ── Basculer automatiquement vers la bonne vue selon la section sauvegardée
    const savedSection = (q as any).section as string | undefined;
    if (savedSection === "comparaison") {
      setViewMode("comparison");
      setComparisonAutoRunToken((t) => t + 1);
      return;
    }
    if (savedSection === "evolution") {
      setViewMode("evolution");
      return;
    }

    // ✅ Auto-exécution immédiate après chargement (vues table / dashboard)
    dispatch(runQuery());
    setResultsActive(true);
    setAnimKey((k) => k + 1);
    setRunBtnAnim(true);
    setTimeout(() => setRunBtnAnim(false), 700);
  };

  const handleDrop = (zone: "rows" | "columns", targetIdx: number | null) => {
    const p = dragPayload.current;
    if (!p) return;
    if (p.source === zone && p.index !== undefined && targetIdx !== null) {
      if (zone === "rows")
        dispatch(reorderRows({ from: p.index, to: targetIdx }));
      else dispatch(reorderColumns({ from: p.index, to: targetIdx }));
    } else if (p.source !== zone) {
      if (zone === "rows") dispatch(moveToRows(p.sel));
      else dispatch(moveToColumns(p.sel));
      setResultsActive(false);
    }
    dragPayload.current = null;
  };

  const measureGroups = measures.reduce<Record<string, typeof measures>>(
    (acc, m) => {
      (acc[measureGroupLabel(m)] = acc[measureGroupLabel(m)] || []).push(m);
      return acc;
    },
    {},
  );

  const dims = dimensions.filter((d) => d.name !== "Measures");
  const hasConfig =
    selMeasures.length > 0 && (rows.length > 0 || columns.length > 0);

  const viewTabs: { key: ViewMode; icon: string; label: string }[] = [
    { key: "table", icon: "⊞", label: "Tableau" },
    { key: "dashboard", icon: "◫", label: "Dashboard" },
    { key: "comparison", icon: "⇌", label: "Comparaison" },
    { key: "evolution", icon: "∿", label: "Évolution" },
  ];

  const card = (extra?: React.CSSProperties): React.CSSProperties => ({
    background: NW.white,
    borderRadius: 12,
    border: `1px solid ${NW.gray200}`,
    boxShadow: "0 1px 8px rgba(24,75,140,0.06)",
    ...extra,
  });
  const inputStyle: React.CSSProperties = {
    border: `1.5px solid ${NW.gray200}`,
    borderRadius: 8,
    padding: "5px 10px",
    fontSize: 12,
    fontFamily: THEME.fonts.body,
    color: NW.gray800,
    outline: "none",
    background: NW.white,
    transition: "border-color 0.15s",
  };

  // ── Display values for masked date inputs (DD/MM/YYYY) ────────────────────
  const [dateFromDisplay, setDateFromDisplay] = React.useState(() =>
    isoToDisplay(dateFrom),
  );
  const [dateToDisplay, setDateToDisplay] = React.useState(() =>
    isoToDisplay(dateTo),
  );
  React.useEffect(() => {
    setDateFromDisplay(isoToDisplay(dateFrom));
  }, [dateFrom]);
  React.useEffect(() => {
    setDateToDisplay(isoToDisplay(dateTo));
  }, [dateTo]);

  // ── Error + shake state for invalid date inputs ───────────────────────────
  const [dateFromError, setDateFromError] = React.useState(false);
  const [dateToError, setDateToError] = React.useState(false);
  const dateFromRef = React.useRef<HTMLInputElement>(null);
  const dateToRef = React.useRef<HTMLInputElement>(null);
  // ── Refs des inputs date natifs cachés (pilotés par l'icône 📅) ──
  const dateFromPickerRef = React.useRef<HTMLInputElement>(null);
  const dateToPickerRef = React.useRef<HTMLInputElement>(null);

  const triggerShake = (
    ref: React.RefObject<HTMLInputElement | null>,
    setError: (v: boolean) => void,
  ) => {
    setError(true);
    ref.current?.classList.remove("nw-date-shake");
    void ref.current?.offsetWidth; // reflow to restart animation
    ref.current?.classList.add("nw-date-shake");
    setTimeout(() => {
      ref.current?.classList.remove("nw-date-shake");
      setError(false);
    }, 400);
  };

  return (
    // ══ LAYOUT PRINCIPAL : SIDEBAR DIMENSIONS (gauche) + CONTENU (droite) ══
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        gap: 14,
        fontFamily: THEME.fonts.body,
        background: NW.gray50,
        padding: "2px 0",
        alignItems: "flex-start",
      }}
    >
      {/* ══ SIDEBAR DIMENSIONS (verticale, gauche) ════════════════════════ */}
      <div
        style={{
          ...card({ overflow: "visible" }),
          width: dimPanelCollapsed ? "auto" : 260,
          flexShrink: 0,
          alignSelf: "flex-start",
          position: "sticky",
          top: 0,
        }}
      >
        {/* Header sidebar */}
        <div
          style={{
            padding: "12px 14px",
            background: `linear-gradient(135deg, ${NW.violetDark} 0%, ${NW.violet} 50%, #1D5BA8 100%)`,
            borderRadius: dimPanelCollapsed ? 12 : "12px 12px 0 0",
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: 16, color: "rgba(255,255,255,0.8)" }}>
            ⬡
          </span>
          {!dimPanelCollapsed && (
            <span
              style={{
                fontFamily: THEME.fonts.heading,
                fontWeight: 700,
                fontSize: 13,
                color: "#FFFFFF",
              }}
            >
              Dimensions
            </span>
          )}
          {cubeLoading && (
            <span className="spinner" style={{ marginLeft: 4 }} />
          )}
          {!dimPanelCollapsed && (
            <span
              style={{
                fontSize: 11,
                color: "rgba(255,255,255,0.45)",
                marginTop: 2,
              }}
            >
              {dimensions
                .filter((d) => d.name !== "Measures")
                .reduce((n, d) => n + d.hierarchies.length, 0)}{" "}
              hiérarchies
            </span>
          )}
          {/* Bouton collapse Dimensions — existant */}
          <CollapseToggleBtn
            collapsed={dimPanelCollapsed}
            onToggle={() => setDimPanelCollapsed((v) => !v)}
            title={
              dimPanelCollapsed
                ? "Afficher les dimensions"
                : "Masquer les dimensions"
            }
          />
        </div>

        {/* Corps sidebar — hiérarchies regroupées par dimension */}
        {!dimPanelCollapsed && (
          <DimensionPanel
            dimensions={dims}
            rows={rows}
            columns={columns}
            loading={cubeLoading}
            onAdd={handleAddSelection}
            onDragStart={(sel) => {
              dragPayload.current = { source: "list", sel };
            }}
          />
        )}
      </div>

      {/* ══ DROITE : MESURES + CONTRÔLES + RÉSULTATS ══════════════════════ */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        {/* ── BLOC MESURES ─────────────────────────────────────────────────── */}
        {/* ⑥ : le header reste toujours visible ; le corps se cache si measuresCollapsed */}
        <div style={card({ overflow: "visible" })}>
          {/* Header Mesures */}
          <div
            style={{
              padding: "12px 18px",
              background: `linear-gradient(135deg, ${NW.violetDark} 0%, ${NW.violet} 50%, #1D5BA8 100%)`,
              borderRadius: measuresCollapsed ? 12 : "12px 12px 0 0",
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              transition: "border-radius 0.2s",
            }}
          >
            <span style={{ color: "#FCE7BF", fontSize: 14 }}>◈</span>
            <span
              style={{
                fontFamily: THEME.fonts.heading,
                fontWeight: 700,
                fontSize: 13,
                color: "#FFFFFF",
              }}
            >
              Mesures
            </span>

            {/* Chips mesures sélectionnées — toujours visibles même si collapsed */}
            <div
              style={{
                display: "flex",
                flexWrap: "nowrap",
                gap: 5,
                overflowX: "auto",
                maxWidth: 400,
                scrollbarWidth: "none",
                flexShrink: 1,
              }}
            >
              {selMeasures.map((name) => {
                const m = measures.find((x) => x.name === name);
                return (
                  <span
                    key={name}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "3px 9px",
                      borderRadius: 99,
                      background: "rgba(125,168,217,0.22)",
                      border: "1px solid rgba(125,168,217,0.45)",
                      color: NW.roseLight,
                      fontSize: 11,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    {m?.caption ?? name}
                    <button
                      onClick={() => {
                        dispatch(toggleMeasure(name));
                        setResultsActive(false);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "rgba(255,255,255,0.75)",
                        cursor: "pointer",
                        padding: 0,
                        fontSize: 14,
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>

            {/* Input ajout mesure */}
            <div style={{ position: "relative", marginLeft: 4 }}>
              <input
                ref={measureInputRef}
                type="text"
                value={measureQuery}
                onChange={(e) => {
                  setMeasureQuery(e.target.value);
                  setMeasureDropOpen(true);
                }}
                onFocus={() => setMeasureDropOpen(true)}
                placeholder="◈ Ajouter une mesure…"
                style={{
                  background: "rgba(255,255,255,0.1)",
                  border: `1px solid ${measureDropOpen ? NW.rose : "rgba(255,255,255,0.2)"}`,
                  borderRadius: 99,
                  padding: "5px 14px",
                  fontSize: 12,
                  color: "#FFFFFF",
                  fontFamily: THEME.fonts.body,
                  outline: "none",
                  width: 200,
                  transition: "border-color 0.15s",
                }}
              />
              {measureDropOpen && measureSuggestions.length > 0 && (
                <div
                  ref={measureDropRef}
                  style={{
                    position: "absolute",
                    top: "calc(100% + 8px)",
                    left: 0,
                    background: NW.white,
                    borderRadius: 10,
                    boxShadow: "0 8px 30px rgba(24,75,140,0.15)",
                    minWidth: 290,
                    maxHeight: 300,
                    overflowY: "auto",
                    zIndex: 999,
                    border: `1.5px solid ${NW.violetMed}`,
                  }}
                >
                  {measureSuggestions.map((m) => {
                    const active = selMeasures.includes(m.name);
                    return (
                      <div
                        key={m.name}
                        onClick={() => handleMeasureSelect(m.name)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "9px 14px",
                          cursor: "pointer",
                          background: active ? NW.roseLight : NW.white,
                          borderBottom: `1px solid ${NW.gray100}`,
                          transition: "background 0.1s",
                        }}
                        onMouseEnter={(e) =>
                          ((e.currentTarget as HTMLElement).style.background =
                            active ? NW.roseMed : NW.violetSoft)
                        }
                        onMouseLeave={(e) =>
                          ((e.currentTarget as HTMLElement).style.background =
                            active ? NW.roseLight : NW.white)
                        }
                      >
                        <span
                          style={{
                            width: 18,
                            textAlign: "center",
                            fontWeight: 800,
                            fontSize: 13,
                            color: active ? NW.rose : NW.gray500,
                            flexShrink: 0,
                          }}
                        >
                          {active ? "✓" : "+"}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: active ? 700 : 500,
                              fontSize: 12,
                              color: active ? NW.roseDark : NW.gray800,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {m.caption ?? m.name}
                          </div>
                          {m.measureGroup && (
                            <div style={{ fontSize: 10, color: NW.gray500 }}>
                              {m.measureGroup}
                            </div>
                          )}
                        </div>
                        {active && (
                          <span
                            style={{
                              background: NW.rose,
                              color: "#FFFFFF",
                              borderRadius: 99,
                              fontSize: 10,
                              padding: "1px 7px",
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            Sélectionnée
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {selMeasures.length > 0 && (
              <span
                style={{
                  background: "rgba(125,168,217,0.18)",
                  color: NW.roseLight,
                  border: "1px solid rgba(125,168,217,0.32)",
                  borderRadius: 99,
                  padding: "1px 9px",
                  fontSize: 11,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {selMeasures.length} mesure{selMeasures.length > 1 ? "s" : ""}
              </span>
            )}

            {/* ⑥ Bouton collapse Mesures */}
            <CollapseToggleBtn
              collapsed={measuresCollapsed}
              onToggle={() => setMeasuresCollapsed((v) => !v)}
              title={
                measuresCollapsed
                  ? "Afficher les groupes de mesures"
                  : "Masquer les groupes de mesures"
              }
            />
          </div>

          {/* Corps Mesures : groupes VENTE / ACHAT — masqué si measuresCollapsed */}
          {!measuresCollapsed && (
            <div
              style={{
                padding: "10px 12px",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {Object.entries(measureGroups).map(([group, ms]) => {
                const PINNED_EXACT = [
                  "qte",
                  "pttc",
                  "remise",
                  "tva",
                  "phtarem",
                ];
                const pinned = ms.filter((m) => {
                  const cap = (m.caption ?? m.name).trim().toLowerCase();
                  const base = cap.replace(/\s*-\s*(achat|vente)$/, "").trim();
                  return PINNED_EXACT.includes(base);
                });
                const others = ms.filter((m) => !pinned.includes(m));
                const isPopupOpen = measurePopupGroup === group;
                const extraSelected = others.filter((m) =>
                  selMeasures.includes(m.name),
                );
                const groupCount = selMeasures.filter((n) =>
                  ms.some((m) => m.name === n),
                ).length;

                return (
                  <div
                    key={group}
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "6px 8px",
                      borderRadius: 8,
                      background: NW.gray50,
                      border: `1px solid ${NW.gray200}`,
                    }}
                  >
                    {/* ── Étiquette groupe (à gauche, largeur fixe) ── */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        minWidth: 64,
                        flexShrink: 0,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 800,
                          color: NW.violet,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                          fontFamily: THEME.fonts.heading,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {group}
                      </span>
                      {groupCount > 0 && (
                        <span
                          style={{
                            background: NW.roseMed,
                            color: NW.roseDark,
                            borderRadius: 99,
                            padding: "0 6px",
                            fontSize: 10,
                            fontWeight: 700,
                            marginTop: 2,
                          }}
                        >
                          {groupCount}✓
                        </span>
                      )}
                    </div>

                    {/* ── Séparateur vertical ── */}
                    <div
                      style={{
                        width: 1.5,
                        alignSelf: "stretch",
                        background: NW.violetMed,
                        borderRadius: 1,
                        flexShrink: 0,
                      }}
                    />

                    {/* ── Chips (inline) ── */}
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 5,
                        alignItems: "center",
                        flex: 1,
                      }}
                    >
                      {/* Chips épinglées (Qte, Pttc, Remise, Tva, Phtarem) */}
                      {pinned.map((m) => {
                        const checked = selMeasures.includes(m.name);
                        return (
                          <button
                            key={m.name}
                            onClick={() => {
                              dispatch(toggleMeasure(m.name));
                              setResultsActive(false);
                            }}
                            style={{
                              padding: "5px 11px",
                              borderRadius: 8,
                              border: `1.5px solid ${checked ? NW.rose : NW.gray200}`,
                              background: checked ? NW.rose : NW.white,
                              color: checked ? "#FFFFFF" : NW.gray700,
                              fontSize: 11,
                              fontWeight: checked ? 700 : 500,
                              cursor: "pointer",
                              transition: "all 0.15s",
                              boxShadow: checked
                                ? "0 2px 8px rgba(125,168,217,0.28)"
                                : "0 1px 3px rgba(15,23,42,0.06)",
                              fontFamily: THEME.fonts.body,
                            }}
                          >
                            {m.caption ?? m.name}
                          </button>
                        );
                      })}

                      {/* Chips pour les mesures extra sélectionnées */}
                      {extraSelected.map((m) => (
                        <button
                          key={m.name}
                          onClick={() => {
                            dispatch(toggleMeasure(m.name));
                            setResultsActive(false);
                          }}
                          style={{
                            padding: "5px 9px",
                            borderRadius: 8,
                            border: `1.5px solid ${NW.violet}`,
                            background: NW.violetLight,
                            color: NW.violetDark,
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            fontFamily: THEME.fonts.body,
                            transition: "all 0.15s",
                          }}
                        >
                          {m.caption ?? m.name}
                          <span
                            style={{
                              fontSize: 14,
                              lineHeight: 1,
                              color: NW.violet,
                              opacity: 0.7,
                            }}
                          >
                            ×
                          </span>
                        </button>
                      ))}

                      {/* Bouton "+" */}
                      {others.length > 0 && (
                        <div style={{ position: "relative", flexShrink: 0 }}>
                          <button
                            onClick={() => {
                              if (!isPopupOpen)
                                setPopupInitialMeasures([...selMeasures]);
                              setMeasurePopupGroup(isPopupOpen ? null : group);
                            }}
                            onMouseEnter={() => setHoveredPlusGroup(group)}
                            onMouseLeave={() => setHoveredPlusGroup(null)}
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: 8,
                              border: `1.5px solid ${isPopupOpen ? NW.violet : NW.gray200}`,
                              background: isPopupOpen ? NW.violet : NW.white,
                              color: isPopupOpen ? "#FFFFFF" : NW.violet,
                              fontSize: 18,
                              fontWeight: 700,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              lineHeight: 1,
                              transition: "all 0.15s",
                              boxShadow: isPopupOpen
                                ? `0 2px 8px rgba(24,75,140,0.30)`
                                : "0 1px 3px rgba(15,23,42,0.06)",
                              flexShrink: 0,
                            }}
                          >
                            +
                          </button>

                          {/* Tooltip */}
                          {hoveredPlusGroup === group && !isPopupOpen && (
                            <div
                              style={{
                                position: "absolute",
                                bottom: "calc(100% + 7px)",
                                left: "50%",
                                transform: "translateX(-50%)",
                                background: NW.gray800,
                                color: "#FFFFFF",
                                fontSize: 11,
                                fontWeight: 600,
                                padding: "5px 10px",
                                borderRadius: 7,
                                whiteSpace: "nowrap",
                                pointerEvents: "none",
                                zIndex: 10000,
                                boxShadow: "0 4px 12px rgba(15,23,42,0.18)",
                                fontFamily: THEME.fonts.body,
                              }}
                            >
                              Ajouter une mesure {group}
                              <div
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  width: 0,
                                  height: 0,
                                  borderLeft: "5px solid transparent",
                                  borderRight: "5px solid transparent",
                                  borderTop: `5px solid ${NW.gray800}`,
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ── Modal popup "autres mesures" ── */}
                    {isPopupOpen && (
                      <>
                        <div
                          onClick={() => setMeasurePopupGroup(null)}
                          style={{
                            position: "fixed",
                            inset: 0,
                            background: "rgba(13,42,80,0.35)",
                            backdropFilter: "blur(2px)",
                            zIndex: 9998,
                          }}
                        />
                        <div
                          ref={measurePopupRef}
                          style={{
                            position: "fixed",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            zIndex: 9999,
                            background: NW.white,
                            borderRadius: 16,
                            boxShadow:
                              "0 24px 80px rgba(24,75,140,0.22), 0 4px 20px rgba(15,23,42,0.12)",
                            width: 340,
                            maxHeight: "70vh",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            animation:
                              "nwFadeSlideIn 0.22s cubic-bezier(0.34,1.56,0.64,1)",
                          }}
                        >
                          <div
                            style={{
                              padding: "14px 18px",
                              background: `linear-gradient(135deg, ${NW.violetDark}, ${NW.violet})`,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <span style={{ color: "#FCE7BF", fontSize: 16 }}>
                              ◈
                            </span>
                            <span
                              style={{
                                flex: 1,
                                fontFamily: THEME.fonts.heading,
                                fontWeight: 700,
                                fontSize: 13,
                                color: "#FFFFFF",
                              }}
                            >
                              {group} — autres mesures
                            </span>
                          </div>
                          <div
                            style={{
                              overflowY: "auto",
                              flex: 1,
                              padding: "6px 0",
                            }}
                          >
                            {others.map((m) => {
                              const checked = selMeasures.includes(m.name);
                              return (
                                <label
                                  key={m.name}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    padding: "10px 18px",
                                    cursor: "pointer",
                                    background: checked
                                      ? NW.roseLight
                                      : "transparent",
                                    borderBottom: `1px solid ${NW.gray100}`,
                                    fontSize: 13,
                                    transition: "background 0.1s",
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!checked)
                                      (
                                        e.currentTarget as HTMLElement
                                      ).style.background = NW.violetSoft;
                                  }}
                                  onMouseLeave={(e) => {
                                    (
                                      e.currentTarget as HTMLElement
                                    ).style.background = checked
                                      ? NW.roseLight
                                      : "transparent";
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => {
                                      dispatch(toggleMeasure(m.name));
                                      setResultsActive(false);
                                    }}
                                    style={{
                                      accentColor: NW.rose,
                                      width: 15,
                                      height: 15,
                                    }}
                                  />
                                  <span
                                    style={{
                                      color: checked ? NW.roseDark : NW.gray700,
                                      fontWeight: checked ? 700 : 500,
                                      fontFamily: THEME.fonts.body,
                                    }}
                                  >
                                    {m.caption ?? m.name}
                                  </span>
                                  {checked && (
                                    <span
                                      style={{
                                        marginLeft: "auto",
                                        background: NW.rose,
                                        color: "#FFFFFF",
                                        borderRadius: 99,
                                        fontSize: 10,
                                        padding: "1px 7px",
                                        fontWeight: 700,
                                      }}
                                    >
                                      ✓
                                    </span>
                                  )}
                                </label>
                              );
                            })}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              padding: "10px 14px",
                              background: NW.gray50,
                              borderTop: `1px solid ${NW.gray200}`,
                              justifyContent: "flex-end",
                            }}
                          >
                            <button
                              onClick={() => {
                                others.forEach((m) => {
                                  const wasChecked =
                                    popupInitialMeasures.includes(m.name);
                                  const isChecked = selMeasures.includes(
                                    m.name,
                                  );
                                  if (wasChecked !== isChecked)
                                    dispatch(toggleMeasure(m.name));
                                });
                                setMeasurePopupGroup(null);
                              }}
                              style={{
                                padding: "7px 16px",
                                borderRadius: 8,
                                border: `1.5px solid ${NW.danger}`,
                                background: NW.white,
                                color: NW.danger,
                                fontWeight: 700,
                                fontSize: 12,
                                fontFamily: THEME.fonts.body,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                              }}
                            >
                              ✕ Annuler
                            </button>
                            <button
                              onClick={() => setMeasurePopupGroup(null)}
                              style={{
                                padding: "7px 18px",
                                borderRadius: 8,
                                border: "none",
                                background: `linear-gradient(135deg, ${NW.violetDark}, ${NW.violet})`,
                                color: "#FFFFFF",
                                fontWeight: 700,
                                fontSize: 12,
                                fontFamily: THEME.fonts.body,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                                boxShadow: "0 2px 8px rgba(24,75,140,0.25)",
                              }}
                            >
                              ✓ Valider
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── BLOC CONTRÔLES (Période + TopN + Solde) ──────────────────────── */}
        {/* ⑦ : le header reste visible ; le corps disparaît si controlsCollapsed */}
        <div style={card({ overflow: "visible" })}>
          {/* Header contrôles */}
          <div
            style={{
              padding: "10px 18px",
              background: `linear-gradient(135deg, ${NW.violetDark} 0%, ${NW.violet} 50%, #1D5BA8 100%)`,
              borderRadius: controlsCollapsed ? 12 : "12px 12px 0 0",
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              transition: "border-radius 0.2s",
            }}
          >
            <span style={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }}>
              📅
            </span>
            <span
              style={{
                fontFamily: THEME.fonts.heading,
                fontWeight: 700,
                fontSize: 13,
                color: "#FFFFFF",
              }}
            >
              Période &amp; Filtres
            </span>

            {/* Résumé compact affiché même quand collapsed */}
            {controlsCollapsed &&
              (dateFrom || dateTo || topN !== null || soldeSelected) && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {(dateFrom || dateTo) && (
                    <span
                      style={{
                        background: "rgba(255,255,255,0.18)",
                        color: "#FFFFFF",
                        borderRadius: 99,
                        padding: "2px 10px",
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      {dateFrom || "…"} → {dateTo || "…"}
                    </span>
                  )}
                  {topN !== null && (
                    <span
                      style={{
                        background: "rgba(252,231,191,0.25)",
                        color: "#FCE7BF",
                        borderRadius: 99,
                        padding: "2px 10px",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      🏆 Top {topN}
                    </span>
                  )}
                  {soldeSelected && (
                    <span
                      style={{
                        background: "rgba(0,160,140,0.28)",
                        color: "#A9DFD7",
                        borderRadius: 99,
                        padding: "2px 10px",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      🏷️ {soldeSelected.code_periode}
                      {selectedDemarquePhase
                        ? ` · ${selectedDemarquePhase}`
                        : ""}
                    </span>
                  )}
                </div>
              )}

            {/* ⑦ Bouton collapse Contrôles */}
            <CollapseToggleBtn
              collapsed={controlsCollapsed}
              onToggle={() => setControlsCollapsed((v) => !v)}
              title={
                controlsCollapsed
                  ? "Afficher les filtres"
                  : "Masquer les filtres"
              }
            />
          </div>

          {/* Corps contrôles — masqué si controlsCollapsed */}
          {!controlsCollapsed && (
            <div style={{ padding: "13px 18px" }}>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 14,
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontFamily: THEME.fonts.heading,
                      fontWeight: 700,
                      fontSize: 12,
                      color: NW.gray800,
                    }}
                  >
                    📅 Période
                  </span>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 12,
                      color: NW.gray500,
                    }}
                  >
                    De :
                    <div
                      style={{ position: "relative", display: "inline-flex" }}
                    >
                      <input
                        ref={dateFromRef}
                        type="text"
                        inputMode="numeric"
                        placeholder="JJ/MM/AAAA"
                        maxLength={10}
                        value={dateFromDisplay}
                        onFocus={() => dateFromPickerRef.current?.blur()}
                        onChange={(e) => {
                          const masked = applyDateMask(e.target.value);
                          setDateFromDisplay(masked);
                          if (dateFromError) setDateFromError(false);
                          if (masked.length === 10) {
                            const iso = displayToIso(masked);
                            const fixed = fixYear(iso);
                            if (isValidDate(fixed)) {
                              const today = new Date()
                                .toISOString()
                                .slice(0, 10);
                              const capped = fixed > today ? today : fixed;
                              dispatch(
                                setDateRange({
                                  dateFrom: capped,
                                  dateTo:
                                    dateTo && capped > dateTo ? "" : dateTo,
                                }),
                              );
                            }
                          }
                        }}
                        onBlur={() => {
                          if (dateFromDisplay.length === 10) {
                            const iso = displayToIso(dateFromDisplay);
                            const fixed = fixYear(iso);
                            if (isValidDate(fixed)) {
                              const today = new Date()
                                .toISOString()
                                .slice(0, 10);
                              const capped = fixed > today ? today : fixed;
                              dispatch(
                                setDateRange({
                                  dateFrom: capped,
                                  dateTo:
                                    dateTo && capped > dateTo ? "" : dateTo,
                                }),
                              );
                            } else {
                              triggerShake(dateFromRef, setDateFromError);
                              dispatch(setDateRange({ dateFrom: "", dateTo }));
                            }
                            setDateFromDisplay(isoToDisplay(dateFrom));
                          } else if (dateFromDisplay === "") {
                            dispatch(setDateRange({ dateFrom: "", dateTo }));
                          } else if (dateFromDisplay.length > 0) {
                            triggerShake(dateFromRef, setDateFromError);
                            setDateFromDisplay(isoToDisplay(dateFrom));
                          }
                        }}
                        style={{
                          ...inputStyle,
                          width: 110,
                          paddingRight: 26,
                          letterSpacing: 0.5,
                          borderColor: dateFromError
                            ? NW.danger
                            : dateFromDisplay.length > 0 &&
                                dateFromDisplay.length < 10
                              ? NW.amber
                              : dateFrom
                                ? NW.violet
                                : NW.gray200,
                          boxShadow: dateFromError
                            ? `0 0 0 2px ${NW.danger}33`
                            : undefined,
                        }}
                      />
                      {/* 📅 Icône — ouvre le calendrier natif */}
                      <button
                        type="button"
                        onClick={() => {
                          const el = dateFromPickerRef.current;
                          if (!el) return;
                          if (typeof (el as any).showPicker === "function")
                            (el as any).showPicker();
                          else el.focus();
                        }}
                        title="Ouvrir le calendrier"
                        style={{
                          position: "absolute",
                          right: 4,
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: 13,
                          padding: 2,
                          lineHeight: 1,
                          color: NW.violet,
                        }}
                      >
                        📅
                      </button>
                      {/* Input date natif caché — pilote le calendrier */}
                      <input
                        ref={dateFromPickerRef}
                        type="date"
                        value={dateFrom || ""}
                        max={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => {
                          const iso = e.target.value;
                          if (!iso) return;
                          const today = new Date().toISOString().slice(0, 10);
                          const capped = iso > today ? today : iso;
                          dispatch(
                            setDateRange({
                              dateFrom: capped,
                              dateTo: dateTo && capped > dateTo ? "" : dateTo,
                            }),
                          );
                          setDateFromDisplay(isoToDisplay(capped));
                        }}
                        style={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          opacity: 0,
                          pointerEvents: "none",
                          border: "none",
                        }}
                        tabIndex={-1}
                      />
                    </div>
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 12,
                      color: NW.gray500,
                    }}
                  >
                    À :
                    <div
                      style={{ position: "relative", display: "inline-flex" }}
                    >
                      <input
                        ref={dateToRef}
                        type="text"
                        inputMode="numeric"
                        placeholder="JJ/MM/AAAA"
                        maxLength={10}
                        value={dateToDisplay}
                        onFocus={() => dateToPickerRef.current?.blur()}
                        onChange={(e) => {
                          const masked = applyDateMask(e.target.value);
                          setDateToDisplay(masked);
                          if (dateToError) setDateToError(false);
                          if (masked.length === 10) {
                            const iso = displayToIso(masked);
                            const fixed = fixYear(iso);
                            if (isValidDate(fixed)) {
                              const today = new Date()
                                .toISOString()
                                .slice(0, 10);
                              const capped = fixed > today ? today : fixed;
                              if (!dateFrom || capped >= dateFrom) {
                                dispatch(
                                  setDateRange({ dateFrom, dateTo: capped }),
                                );
                              }
                            }
                          }
                        }}
                        onBlur={() => {
                          if (dateToDisplay.length === 10) {
                            const iso = displayToIso(dateToDisplay);
                            const fixed = fixYear(iso);
                            if (isValidDate(fixed)) {
                              const today = new Date()
                                .toISOString()
                                .slice(0, 10);
                              const capped = fixed > today ? today : fixed;
                              if (!dateFrom || capped >= dateFrom) {
                                dispatch(
                                  setDateRange({ dateFrom, dateTo: capped }),
                                );
                              } else {
                                triggerShake(dateToRef, setDateToError);
                                dispatch(
                                  setDateRange({ dateFrom, dateTo: "" }),
                                );
                              }
                            } else {
                              triggerShake(dateToRef, setDateToError);
                              dispatch(setDateRange({ dateFrom, dateTo: "" }));
                            }
                            setDateToDisplay(isoToDisplay(dateTo));
                          } else if (dateToDisplay === "") {
                            dispatch(setDateRange({ dateFrom, dateTo: "" }));
                          } else if (dateToDisplay.length > 0) {
                            triggerShake(dateToRef, setDateToError);
                            setDateToDisplay(isoToDisplay(dateTo));
                          }
                        }}
                        style={{
                          ...inputStyle,
                          width: 110,
                          paddingRight: 26,
                          letterSpacing: 0.5,
                          borderColor: dateToError
                            ? NW.danger
                            : dateToDisplay.length > 0 &&
                                dateToDisplay.length < 10
                              ? NW.amber
                              : dateTo
                                ? NW.violet
                                : NW.gray200,
                          boxShadow: dateToError
                            ? `0 0 0 2px ${NW.danger}33`
                            : undefined,
                        }}
                      />
                      {/* 📅 Icône — ouvre le calendrier natif */}
                      <button
                        type="button"
                        onClick={() => {
                          const el = dateToPickerRef.current;
                          if (!el) return;
                          if (typeof (el as any).showPicker === "function")
                            (el as any).showPicker();
                          else el.focus();
                        }}
                        title="Ouvrir le calendrier"
                        style={{
                          position: "absolute",
                          right: 4,
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: 13,
                          padding: 2,
                          lineHeight: 1,
                          color: NW.rose,
                        }}
                      >
                        📅
                      </button>
                      {/* Input date natif caché — pilote le calendrier */}
                      <input
                        ref={dateToPickerRef}
                        type="date"
                        value={dateTo || ""}
                        min={dateFrom || undefined}
                        max={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => {
                          const iso = e.target.value;
                          if (!iso) return;
                          const today = new Date().toISOString().slice(0, 10);
                          const capped = iso > today ? today : iso;
                          if (!dateFrom || capped >= dateFrom) {
                            dispatch(
                              setDateRange({ dateFrom, dateTo: capped }),
                            );
                            setDateToDisplay(isoToDisplay(capped));
                          }
                        }}
                        style={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          opacity: 0,
                          pointerEvents: "none",
                          border: "none",
                        }}
                        tabIndex={-1}
                      />
                    </div>
                  </label>
                  {(["week", "month", "year"] as const).map((t) => (
                    <button
                      key={t}
                      onClick={() => dispatch(setDateRange(getQuickPeriod(t)))}
                      style={{
                        fontSize: 11,
                        padding: "3px 11px",
                        borderRadius: 99,
                        cursor: "pointer",
                        border: `1px solid ${NW.violet}55`,
                        background: NW.violetLight,
                        color: NW.violet,
                        fontWeight: 700,
                        fontFamily: THEME.fonts.body,
                      }}
                    >
                      {t === "week" ? "7j" : t === "month" ? "1m" : "1an"}
                    </button>
                  ))}
                  {(dateFrom || dateTo) && (
                    <button
                      onClick={() =>
                        dispatch(setDateRange({ dateFrom: "", dateTo: "" }))
                      }
                      style={{
                        fontSize: 11,
                        padding: "3px 10px",
                        borderRadius: 99,
                        border: "1.5px solid #D64545",
                        background: "#D64545",
                        color: "#FFFFFF",
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: THEME.fonts.body,
                      }}
                    >
                      ✕ Effacer
                    </button>
                  )}
                </div>
                <div
                  style={{
                    width: 1,
                    height: 28,
                    background: NW.gray200,
                    flexShrink: 0,
                  }}
                />
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      fontFamily: THEME.fonts.heading,
                      fontWeight: 700,
                      fontSize: 12,
                      color: NW.gray800,
                    }}
                  >
                    🏆 Top N
                  </span>
                  {[5, 10, 20, 50].map((n) => (
                    <button
                      key={n}
                      onClick={() => dispatch(setTopN(topN === n ? null : n))}
                      style={{
                        fontSize: 11,
                        padding: "3px 10px",
                        borderRadius: 99,
                        cursor: "pointer",
                        border: `1.5px solid ${topN === n ? NW.violet : NW.gray200}`,
                        background: topN === n ? NW.violet : NW.white,
                        color: topN === n ? "#FFFFFF" : NW.gray500,
                        fontWeight: topN === n ? 700 : 500,
                        transition: "all 0.15s",
                      }}
                    >
                      Top {n}
                    </button>
                  ))}
                  <input
                    type="number"
                    min={1}
                    max={9999}
                    value={
                      topN !== null && ![5, 10, 20, 50].includes(topN)
                        ? topN
                        : ""
                    }
                    placeholder="N"
                    onChange={(e) => {
                      const v = parseInt(e.target.value);
                      dispatch(setTopN(isNaN(v) || v < 1 ? null : v));
                    }}
                    style={{ ...inputStyle, width: 52 }}
                  />
                  {topN !== null && (
                    <button
                      onClick={() => dispatch(setTopN(null))}
                      className="btn-ghost"
                      style={{ fontSize: 11, padding: "3px 10px" }}
                    >
                      Tout
                    </button>
                  )}
                </div>

                {/* ── Séparateur ── */}
                <div
                  style={{
                    width: 1,
                    height: 28,
                    background: NW.gray200,
                    flexShrink: 0,
                  }}
                />

                {/* ── 🏷️ Solde / Périodes ── */}
                <div
                  ref={soldeDropRef}
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span
                    style={{
                      fontFamily: THEME.fonts.heading,
                      fontWeight: 700,
                      fontSize: 12,
                      color: NW.gray800,
                    }}
                  >
                    🏷️ Solde
                  </span>
                  <button
                    onClick={handleSoldeOpen}
                    style={{
                      fontSize: 11,
                      padding: "4px 13px",
                      borderRadius: 99,
                      cursor: "pointer",
                      border: `1.5px solid ${soldeSelected ? "#00A08C" : NW.gray200}`,
                      background: soldeSelected ? "#D3EFEA" : NW.white,
                      color: soldeSelected ? "#006357" : NW.gray500,
                      fontWeight: soldeSelected ? 700 : 500,
                      transition: "all 0.15s",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      whiteSpace: "nowrap",
                      fontFamily: THEME.fonts.body,
                    }}
                  >
                    {soldeSelected
                      ? soldeSelected.code_periode
                      : "Choisir période"}
                    <span style={{ fontSize: 9, opacity: 0.6 }}>▾</span>
                  </button>
                  {soldeSelected && (
                    <button
                      onClick={handleSoldeClear}
                      title="Effacer la sélection solde"
                      style={{
                        fontSize: 11,
                        padding: "3px 9px",
                        borderRadius: 99,
                        cursor: "pointer",
                        border: `1px solid #A9DFD7`,
                        background: "#EDF8F6",
                        color: "#006357",
                        fontWeight: 700,
                        fontFamily: THEME.fonts.body,
                      }}
                    >
                      ✕
                    </button>
                  )}

                  {/* Dropdown périodes */}
                  {soldeOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 8px)",
                        left: 0,
                        background: NW.white,
                        borderRadius: 12,
                        boxShadow: "0 8px 30px rgba(24,75,140,0.15)",
                        minWidth: 300,
                        maxHeight: 340,
                        overflowY: "auto",
                        zIndex: 9999,
                        border: `1.5px solid #A9DFD7`,
                      }}
                    >
                      {/* En-tête */}
                      <div
                        style={{
                          padding: "10px 16px",
                          background:
                            "linear-gradient(135deg, #006357, #00A08C)",
                          borderRadius: "10px 10px 0 0",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span style={{ fontSize: 14 }}>🏷️</span>
                        <span
                          style={{
                            fontFamily: THEME.fonts.heading,
                            fontWeight: 700,
                            fontSize: 12,
                            color: "#FFFFFF",
                          }}
                        >
                          Périodes de Solde
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            color: "rgba(255,255,255,0.6)",
                            marginLeft: 4,
                          }}
                        >
                          (dates auto + filtre articles)
                        </span>
                      </div>

                      {/* Contenu */}
                      {soldeLoading ? (
                        <div
                          style={{
                            padding: 20,
                            textAlign: "center",
                            color: NW.gray500,
                            fontSize: 12,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                          }}
                        >
                          <span className="spinner" />
                          Chargement des périodes…
                        </div>
                      ) : soldeError ? (
                        <div
                          style={{
                            padding: 16,
                            color: NW.danger,
                            fontSize: 12,
                            background: NW.dangerLight,
                            margin: 10,
                            borderRadius: 8,
                          }}
                        >
                          ⚠ {soldeError}
                        </div>
                      ) : soldePeriodes.length === 0 ? (
                        <div
                          style={{
                            padding: 20,
                            textAlign: "center",
                            color: NW.gray500,
                            fontSize: 12,
                            fontStyle: "italic",
                          }}
                        >
                          Aucune période de solde disponible
                        </div>
                      ) : (
                        soldePeriodes.map((p) => {
                          const isActive =
                            soldeSelected?.code_periode === p.code_periode;
                          const fmtDate = (d: string | null) =>
                            d ? new Date(d).toLocaleDateString("fr-FR") : "—";
                          return (
                            <div
                              key={p.code_periode}
                              onClick={() => handleSoldePeriodeSelect(p)}
                              style={{
                                padding: "10px 16px",
                                cursor: "pointer",
                                background: isActive ? "#D3EFEA" : NW.white,
                                borderBottom: `1px solid ${NW.gray100}`,
                                transition: "background 0.1s",
                                display: "flex",
                                flexDirection: "column",
                                gap: 3,
                              }}
                              onMouseEnter={(e) =>
                                ((
                                  e.currentTarget as HTMLElement
                                ).style.background = isActive
                                  ? "#A9DFD7"
                                  : "#F8FAFC")
                              }
                              onMouseLeave={(e) =>
                                ((
                                  e.currentTarget as HTMLElement
                                ).style.background = isActive
                                  ? "#D3EFEA"
                                  : NW.white)
                              }
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                {isActive && (
                                  <span
                                    style={{
                                      color: "#00A08C",
                                      fontWeight: 800,
                                      fontSize: 13,
                                      flexShrink: 0,
                                    }}
                                  >
                                    ✓
                                  </span>
                                )}
                                <span
                                  style={{
                                    fontWeight: 700,
                                    fontSize: 13,
                                    color: isActive ? "#006357" : NW.gray800,
                                    fontFamily: THEME.fonts.heading,
                                  }}
                                >
                                  {p.code_periode}
                                </span>
                              </div>
                              <div
                                style={{
                                  fontSize: 11,
                                  color: NW.gray500,
                                  display: "flex",
                                  gap: 10,
                                  paddingLeft: isActive ? 21 : 0,
                                }}
                              >
                                <span>
                                  📅 {fmtDate(p.date_debut)} →{" "}
                                  {fmtDate(p.date_fin)}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
                {/* Indicateur articles filtrés */}
                {soldeSelected && soldeArticleCodes.size > 0 && (
                  <span
                    style={{
                      background: "#D3EFEA",
                      color: "#006357",
                      border: "1px solid #A9DFD7",
                      borderRadius: 99,
                      padding: "2px 10px",
                      fontSize: 11,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    🏷️ {soldeArticleCodes.size} articles
                  </span>
                )}

                {/* ── Bouton Démarque ── */}
                <div
                  ref={demarqueDropRef}
                  style={{
                    position: "relative",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  <button
                    onClick={() => soldeSelected && setDemarqueOpen((v) => !v)}
                    title={
                      soldeSelected
                        ? `Phases de démarque de ${soldeSelected.code_periode}`
                        : "Sélectionnez d'abord une période de solde"
                    }
                    disabled={!soldeSelected}
                    style={{
                      fontSize: 11,
                      padding: "4px 13px",
                      borderRadius: 99,
                      cursor: soldeSelected ? "pointer" : "not-allowed",
                      border: `1.5px solid ${
                        selectedDemarquePhase
                          ? "#1D5BA8"
                          : demarqueOpen
                            ? "#1D5BA8"
                            : soldeSelected
                              ? "#1D5BA855"
                              : NW.gray200
                      }`,
                      background: selectedDemarquePhase
                        ? "#1D5BA8"
                        : demarqueOpen
                          ? "#EEF4FB"
                          : soldeSelected
                            ? "#F1F5F9"
                            : NW.gray100,
                      color: selectedDemarquePhase
                        ? "#FFFFFF"
                        : soldeSelected
                          ? "#184B8C"
                          : NW.gray300,
                      fontWeight: soldeSelected ? 700 : 500,
                      opacity: soldeSelected ? 1 : 0.55,
                      transition: "all 0.15s",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      whiteSpace: "nowrap",
                      fontFamily: THEME.fonts.body,
                      boxShadow: selectedDemarquePhase
                        ? "0 2px 8px rgba(29,91,168,0.35)"
                        : demarqueOpen
                          ? "0 0 0 2px #D7E5F5"
                          : "none",
                    }}
                  >
                    📉 {selectedDemarquePhase ?? "Démarque"}
                    <span style={{ fontSize: 9, opacity: 0.7 }}>▾</span>
                  </button>

                  {/* Bouton × effacer la démarque */}
                  {selectedDemarquePhase && (
                    <button
                      onClick={() => {
                        setSelectedDemarquePhase(null);
                        dispatch(
                          setDateRange({
                            dateFrom:
                              soldeSelected?.date_debut?.slice(0, 10) ?? "",
                            dateTo: soldeSelected?.date_fin?.slice(0, 10) ?? "",
                          }),
                        );
                      }}
                      title="Effacer la démarque sélectionnée"
                      style={{
                        marginLeft: 4,
                        fontSize: 11,
                        padding: "3px 9px",
                        borderRadius: 99,
                        cursor: "pointer",
                        border: "1px solid #B0CBEB",
                        background: "#EEF4FB",
                        color: "#184B8C",
                        fontWeight: 700,
                        fontFamily: THEME.fonts.body,
                      }}
                    >
                      ✕
                    </button>
                  )}
                  {/* Dropdown démarque */}
                  {demarqueOpen &&
                    soldeSelected &&
                    (() => {
                      const phases = buildDemarquePhases(soldeSelected);
                      return (
                        <div
                          style={{
                            position: "absolute",
                            top: "calc(100% + 8px)",
                            left: 0,
                            background: NW.white,
                            borderRadius: 12,
                            boxShadow: "0 8px 30px rgba(24,75,140,0.18)",
                            minWidth: 290,
                            zIndex: 9999,
                            border: "1.5px solid #D7E5F5",
                            overflow: "hidden",
                          }}
                        >
                          {/* Header */}
                          <div
                            style={{
                              padding: "10px 16px",
                              background:
                                "linear-gradient(135deg, #184B8C, #184B8C)",
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <span style={{ fontSize: 14 }}>📉</span>
                            <div>
                              <div
                                style={{
                                  fontFamily: THEME.fonts.heading,
                                  fontWeight: 700,
                                  fontSize: 12,
                                  color: "#FFFFFF",
                                }}
                              >
                                Phases de démarque
                              </div>
                              <div
                                style={{
                                  fontSize: 10,
                                  color: "rgba(255,255,255,0.6)",
                                }}
                              >
                                {soldeSelected.code_periode}
                              </div>
                            </div>
                          </div>

                          {/* Phases list */}
                          {phases.length === 0 ? (
                            <div
                              style={{
                                padding: "16px 16px",
                                textAlign: "center",
                                color: NW.gray500,
                                fontSize: 12,
                                fontStyle: "italic",
                              }}
                            >
                              Aucune phase de démarque pour cette période
                            </div>
                          ) : (
                            phases.map((phase, idx) => (
                              <div
                                key={idx}
                                onClick={() => {
                                  dispatch(
                                    setDateRange({
                                      dateFrom: phase.dateFrom,
                                      dateTo: phase.dateTo,
                                    }),
                                  );
                                  setSelectedDemarquePhase(phase.label);
                                  setDemarqueOpen(false);
                                  setResultsActive(false);
                                }}
                                style={{
                                  padding: "10px 16px",
                                  cursor: "pointer",
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 3,
                                  borderBottom:
                                    idx < phases.length - 1
                                      ? `1px solid ${NW.gray100}`
                                      : "none",
                                  transition: "background 0.1s",
                                }}
                                onMouseEnter={(e) =>
                                  ((
                                    e.currentTarget as HTMLElement
                                  ).style.background = NW.violetSoft)
                                }
                                onMouseLeave={(e) =>
                                  ((
                                    e.currentTarget as HTMLElement
                                  ).style.background = NW.white)
                                }
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 7,
                                  }}
                                >
                                  <span style={{ fontSize: 13 }}>
                                    {phase.icon}
                                  </span>
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      fontSize: 13,
                                      color: NW.violetDark,
                                      fontFamily: THEME.fonts.heading,
                                    }}
                                  >
                                    {phase.label}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: NW.gray500,
                                    paddingLeft: 20,
                                  }}
                                >
                                  📅{" "}
                                  {new Date(phase.dateFrom).toLocaleDateString(
                                    "fr-FR",
                                  )}{" "}
                                  →{" "}
                                  {phase.dateTo
                                    ? new Date(phase.dateTo).toLocaleDateString(
                                        "fr-FR",
                                      )
                                    : "—"}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      );
                    })()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions + onglets vue */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          {viewMode !== "comparison" && viewMode !== "evolution" && (
            // FIX ③ : animation nwRunPulse sur le bouton à chaque exécution
            <button
              className={`btn-primary${runBtnAnim ? " nw-run-btn-active" : ""}`}
              onClick={handleRunQuery}
              style={{
                background: NW.violet,
                border: "none",
                borderRadius: 9,
                padding: "8px 20px",
                color: "#FFFFFF",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 7,
                boxShadow: `0 4px 14px rgba(24,75,140,0.25)`,
                fontFamily: THEME.fonts.body,
                transition: "transform 0.1s, box-shadow 0.1s",
              }}
            >
              ▶ Exécuter
            </button>
          )}
          <button
            className="btn-ghost"
            onClick={() => {
              dispatch(resetQuery());
              setResultsActive(false);
            }}
            style={{
              fontSize: 12,
              padding: "7px 14px",
              borderRadius: 9,
              border: `1.5px solid ${NW.gray200}`,
              background: NW.white,
              color: NW.gray700,
              cursor: "pointer",
              fontFamily: THEME.fonts.body,
            }}
          >
            ↺ Reset
          </button>
          {!hasConfig && (
            <div style={{ display: "flex", gap: 6 }}>
              {selMeasures.length === 0 && (
                <span
                  style={{
                    background: NW.dangerLight,
                    color: NW.danger,
                    border: `1px solid ${NW.danger}33`,
                    borderRadius: 99,
                    padding: "3px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  ⚠ Sélectionnez une mesure
                </span>
              )}
              {rows.length === 0 && columns.length === 0 && (
                <span
                  style={{
                    background: NW.amberLight,
                    color: NW.amber,
                    border: `1px solid ${NW.amber}33`,
                    borderRadius: 99,
                    padding: "3px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  ⚠ Ajoutez une dimension
                </span>
              )}
            </div>
          )}
          {/* ── Onglets vue + Enregistrer + Requêtes sauvegardées ─────────────── */}
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "nowrap",
            }}
          >
            {/* Bouton Enregistrer — actif seulement après un Exécuter */}
            <button
              onClick={() => setShowSaveModal(true)}
              disabled={!resultsActive}
              title={
                resultsActive
                  ? "Enregistrer cette requête"
                  : "Exécutez d'abord une requête"
              }
              style={{
                background: resultsActive
                  ? NW.violet
                  : NW.gray200,
                border: "none",
                borderRadius: 9,
                padding: "7px 16px",
                color: resultsActive ? "#FFFFFF" : NW.gray500,
                fontWeight: 700,
                fontSize: 12,
                cursor: resultsActive ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                gap: 6,
                boxShadow: resultsActive
                  ? `0 3px 10px rgba(24,75,140,0.22)`
                  : "none",
                fontFamily: THEME.fonts.body,
                transition: "all 0.2s",
                opacity: resultsActive ? 1 : 0.52,
                whiteSpace: "nowrap",
              }}
            >
              💾 Enregistrer
            </button>

            <SavedQueriesDropdown
              onSelect={handleLoadSavedQuery}
              refreshToken={saveRefreshToken}
              currentType="mdx"
            />

            {/* Séparateur */}
            <div
              style={{
                width: 1,
                height: 28,
                background: NW.gray200,
                flexShrink: 0,
              }}
            />

            {/* Onglets */}
            <div
              style={{
                display: "flex",
                background: NW.white,
                border: `1.5px solid ${NW.gray200}`,
                borderRadius: 10,
                overflow: "hidden",
                boxShadow: "0 1px 4px rgba(15,23,42,0.05)",
              }}
            >
              {viewTabs.map((tab) => {
                const active = viewMode === tab.key;
                const activeBg =
                  tab.key === "table"
                    ? NW.violet
                    : NW.violet;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setViewMode(tab.key)}
                    style={{
                      padding: "7px 17px",
                      border: "none",
                      cursor: "pointer",
                      fontFamily: THEME.fonts.body,
                      fontSize: 12,
                      background: active ? activeBg : "transparent",
                      color: active ? "#FFFFFF" : NW.gray500,
                      fontWeight: active ? 700 : 500,
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      transition: "all 0.15s",
                      borderRight: `1px solid ${NW.gray200}`,
                    }}
                  >
                    <span style={{ fontSize: 13 }}>{tab.icon}</span>
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Barre chips Lignes / Colonnes */}
        {(rows.length > 0 || columns.length > 0) && (
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
              padding: "8px 14px",
              background: NW.white,
              borderRadius: 10,
              border: `1.5px solid ${NW.gray200}`,
              boxShadow: "0 1px 6px rgba(24,75,140,0.07)",
            }}
          >
            {rows.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: 5,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    color: NW.violet,
                    fontWeight: 700,
                    fontSize: 11,
                    whiteSpace: "nowrap",
                    fontFamily: THEME.fonts.heading,
                  }}
                >
                  ↕ Lignes
                </span>
                {rows.map((sel, i) => (
                  <span
                    key={i}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = "move";
                      dragPayload.current = { source: "rows", index: i, sel };
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "4px 10px",
                      borderRadius: 8,
                      background: NW.violet,
                      color: "#FFFFFF",
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      cursor: "grab",
                      boxShadow: "0 2px 8px rgba(24,75,140,0.2)",
                    }}
                  >
                    <span style={{ fontSize: 9, opacity: 0.5 }}>⠿</span>
                    <span>
                      {sel.hierarchyName ?? sel.dimensionName}
                      {sel.levelName && sel.levelName !== sel.hierarchyName && (
                        <span
                          style={{
                            fontSize: 10,
                            opacity: 0.7,
                            marginLeft: 4,
                          }}
                        >
                          · {sel.levelName}
                        </span>
                      )}
                    </span>
                    {/* FIX ② : clic × ne vide plus la table */}
                    <button
                      onClick={() => handleRemoveFromRows(i)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "rgba(255,255,255,0.8)",
                        fontSize: 15,
                        lineHeight: 1,
                        padding: "0 2px",
                      }}
                      title="Retirer (la table reste affichée)"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            {rows.length > 0 && columns.length > 0 && (
              <div
                style={{
                  width: 1,
                  height: 22,
                  background: NW.gray200,
                  flexShrink: 0,
                }}
              />
            )}
            {columns.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: 5,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    color: NW.rose,
                    fontWeight: 700,
                    fontSize: 11,
                    whiteSpace: "nowrap",
                    fontFamily: THEME.fonts.heading,
                  }}
                >
                  ↔ Colonnes
                </span>
                {columns.map((sel, i) => (
                  <span
                    key={i}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = "move";
                      dragPayload.current = {
                        source: "columns",
                        index: i,
                        sel,
                      };
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "4px 10px",
                      borderRadius: 8,
                      background: NW.rose,
                      color: "#FFFFFF",
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      cursor: "grab",
                      boxShadow: "0 2px 8px rgba(125,168,217,0.2)",
                    }}
                  >
                    <span style={{ fontSize: 9, opacity: 0.5 }}>⠿</span>
                    <span>
                      {sel.hierarchyName ?? sel.dimensionName}
                      {sel.levelName && sel.levelName !== sel.hierarchyName && (
                        <span
                          style={{
                            fontSize: 10,
                            opacity: 0.7,
                            marginLeft: 4,
                          }}
                        >
                          · {sel.levelName}
                        </span>
                      )}
                    </span>
                    {/* FIX ② : clic × ne vide plus la table */}
                    <button
                      onClick={() => handleRemoveFromColumns(i)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "rgba(255,255,255,0.8)",
                        fontSize: 15,
                        lineHeight: 1,
                        padding: "0 2px",
                      }}
                      title="Retirer (la table reste affichée)"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Zone des résultats */}
        <div style={card({ padding: 18, minHeight: 240 })}>
          {viewMode === "table" && (
            <PivotMatrix
              dragPayload={dragPayload}
              onDrop={handleDrop}
              active={resultsActive}
              animKey={animKey}
            />
          )}
          {viewMode === "dashboard" && <Dashboard />}
          {viewMode === "comparison" && (
            <ComparisonView
              initialComparisonPeriods={savedComparisonPeriods}
              autoRunToken={comparisonAutoRunToken}
            />
          )}
          {viewMode === "evolution" && <EvolutionView />}
        </div>
      </div>

      {showSaveModal && (
        <SaveQueryModal
          onClose={() => setShowSaveModal(false)}
          onSaved={() => {
            setSaveRefreshToken((t) => t + 1);
          }}
          type="mdx"
          section={viewMode}
          overrideSoldeCodePeriode={soldeSelected?.code_periode ?? null}
          overrideDemarquePhase={selectedDemarquePhase}
        />
      )}
    </div>
  );
};

export default QueryBuilder;